/**
 * Portland Configurator JavaScript
 * Handles variant selection, step transitions, and configuration options
 * 
 * NOTE: This is an independent file for the Portland model.
 * It does not share any code with Aspect, Summit, or Signature models.
 */

const DEBUG = false;
const log = (...args) => DEBUG && console.log('[PortlandModal]', ...args);

// ============================================================================
// UTILITIES
// ============================================================================

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => ctx.querySelectorAll(sel);

/** Query with data-attribute fallback to class selector */
const q = (dataAttr, classSelector, ctx = document) => 
  $(dataAttr, ctx) || $(classSelector, ctx);

const qa = (dataAttr, classSelector, ctx = document) => {
  const result = $$(dataAttr, ctx);
  return result.length > 0 ? result : $$(classSelector, ctx);
};

// DRY: shared in `assets/configurator-shared.js`
const normalizeNumber = (...args) => window.ConfiguratorShared.normalizeNumber(...args);
const formatPrice = (dollars) => window.ConfiguratorShared.formatMoneyFromDollars(dollars, { noDecimals: true });

/** Format option value for display (kebab-case to Title Case) */
const formatOptionValue = (value) => value ? value.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '';

/** Format section key to display name */
const formatSectionName = (key) => key.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

/** Extract mesh groups array from various formats */
const extractMeshGroups = (mg) => {
  if (!mg) return [];
  if (Array.isArray(mg)) return mg;
  if (mg.enabled) return mg.enabled;
  return [];
};

// ============================================================================
// PORTLAND CONFIGURATOR CLASS
// ============================================================================

class PortlandConfigurator {
  static DISPLAY_NAME_OVERRIDES = {
    exterior: 'Glass', roof: 'Roof', siding: 'Siding',
    layout: 'Layout', kitchen: 'Kitchen', orientation: 'Orientation'
  };

  static SECTION_GROUPS = {
    'Flooring': ['mainFloorFinish', 'bathFloorFinish', 'flooring'],
    'Finishes': ['fixtureFinish', 'counterFinish', 'cabinetFinish', 'finishes']
  };

  constructor() {
    log('🚀 PortlandConfigurator constructor called');
    
    this.step1Section = q('[data-configurator-step="1"]', '.portland-step-1');
    this.step2Section = q('[data-configurator-step="2"]', '[data-portland-step-2]');
    
    log('Step1 section found:', !!this.step1Section);
    
    if (!this.step1Section) {
      console.error('[PortlandModal] ❌ Step 1 section not found! Looking for [data-configurator-step="1"] or .portland-step-1');
      return;
    }

    // Query variant cards - try both selectors
    this.variantCards = qa('[data-variant-card]', '.portland-variant-card', this.step1Section);
    log('Found', this.variantCards.length, 'variant cards');
    
    // Log first card details for verification
    if (this.variantCards.length > 0) {
      const firstCard = this.variantCards[0];
      log('First card sample:', {
        hasDataAttribute: firstCard.hasAttribute('data-variant-card'),
        variantId: firstCard.dataset.variantId,
        classList: firstCard.className
      });
    }
    
    this.priceDisplay = $('[data-product-price]', this.step1Section);
    this.comparePriceDisplay = $('[data-compare-price]', this.step1Section);
    this.shellPriceDisplay = $('[data-shell-price]', this.step1Section);
    this.thumbnails = qa('[data-product-thumbnail]', '.portland-thumbnail', this.step1Section);
    
    log('Price display found:', !!this.priceDisplay);
    log('Compare price display found:', !!this.comparePriceDisplay);
    
    // Get main image from Swiper structure or fallback to original selector
    const mainSwiperEl = this.step1Section?.querySelector('[data-product-main-swiper]');
    if (mainSwiperEl) {
      const firstSlide = mainSwiperEl.querySelector('swiper-slide');
      this.mainImage = firstSlide?.querySelector('[data-product-main-image]') || 
                       q('[data-product-main-image]', '.portland-main-image', this.step1Section);
    } else {
      this.mainImage = q('[data-product-main-image]', '.portland-main-image', this.step1Section);
    }
    
    this.radioButtons = $$('input[type="radio"][name="variant-selection"]', this.step1Section);
    log('Found', this.radioButtons.length, 'radio buttons');
    
    this.designOwnBtn = $('[data-design-your-own]', this.step1Section);

    this.currentConfig = {};
    this.basePrice = 0;
    this.configPrice = 0;
    this.permitPlanSelected = 'exclude';
    this.permitPlanPrice = 0;
    this.foundationTypeSelected = 'wood-frame';
    this.userFormData = null;
    this.finalPriceData = null;
    this.finalPriceFetchTimer = null;
    this.finalPriceAbort = null;
    this.finalPriceLastPayloadKey = null;
    this.steps = [];
    this.currentStep = 1;
    this.selectedVariant = null;

    if (this.variantCards.length === 0) {
      console.error('[PortlandModal] ❌ No variant cards found! Cannot initialize.');
      console.error('HTML structure check:');
      console.error('- Looking for: [data-variant-card] or .portland-variant-card');
      console.error('- Inside:', this.step1Section);
      return;
    }
    
    log('✓ Constructor complete, calling init()...');
    this.init();
  }

  init() {
    log('PortlandConfigurator initializing...', {
      variantCards: this.variantCards.length,
      radioButtons: this.radioButtons.length,
      priceDisplay: !!this.priceDisplay,
      comparePriceDisplay: !!this.comparePriceDisplay
    });
    
    this.setupVariantSelection();
    this.setupRadioChangeEvents();
    this.setupThumbnailSwitching();
    this.setupDesignOwnButton();    
    
    // Initialize slider with selected variant image on page load
    const selectedCard = this.step1Section?.querySelector('[data-variant-card].selected, .portland-variant-card.selected');
    if (selectedCard) {
      const { variantImageId, variantImageSrc, variantPrice } = selectedCard.dataset;
      if (variantPrice) {
        this.basePrice = parseInt(variantPrice, 10);
      }
      if (variantImageId && variantImageSrc) {
        this.waitForSwiperReady().then(() => {
          this.updateSliderForVariant(variantImageId, variantImageSrc);
        });
      }
    }
    
    if (this.step2Section) this.setupStep2();
    
    log('PortlandConfigurator initialized successfully');
  }

  // Wait for Swiper to be ready using event-driven approach
  waitForSwiperReady() {
    return new Promise((resolve) => {
      const mainSwiperEl = this.step1Section?.querySelector('[data-product-main-swiper]');
      if (!mainSwiperEl) {
        resolve();
        return;
      }

      if (mainSwiperEl.swiper) {
        resolve();
        return;
      }

      let attempts = 0;
      const maxAttempts = 60;
      
      const checkReady = () => {
        if (mainSwiperEl.swiper) {
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          requestAnimationFrame(checkReady);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(checkReady);
    });
  }

  // ============================================================================
  // STEP 1: VARIANT SELECTION
  // ============================================================================

  setupVariantSelection() {
    log('Setting up variant selection for', this.variantCards.length, 'cards');
    
    if (this.variantCards.length === 0) {
      console.error('[PortlandModal] No variant cards found! Check HTML structure.');
      return;
    }
    
    this.variantCards.forEach((card, index) => {
      log('Setting up card', index, 'with variant ID:', card.dataset.variantId);
      
      // Use direct event handler with proper binding
      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        log('✅ Variant card clicked:', index, card.dataset.variantId);
        this.handleVariantSelection(card);
        return false;
      };
      
      // Remove old listeners and add new one
      card.removeEventListener('click', clickHandler);
      card.addEventListener('click', clickHandler, { passive: false });
      
      // Also try mousedown as backup
      card.addEventListener('mousedown', (e) => {
        log('Mousedown detected on card', index);
      });
      
      log('✓ Event listener attached to card', index);
    });
    
    log('✓ All variant selection handlers setup complete');
  }

  setupRadioChangeEvents() {
    log('Setting up radio change events for', this.radioButtons.length, 'radios');
    
    if (this.radioButtons.length === 0) {
      console.error('[PortlandModal] No radio buttons found! Check HTML structure.');
      return;
    }
    
    this.radioButtons.forEach((radio, index) => {
      log('Setting up radio', index, 'with ID:', radio.id);
      
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          log('✅ Radio changed:', radio.id);
          const card = e.target.closest('[data-variant-card], .portland-variant-card');
          if (card) {
            log('Card found for radio:', card.dataset.variantId);
            this.handleVariantSelection(card);
          } else {
            console.error('[PortlandModal] Radio changed but parent card not found');
          }
        }
      });
    });
    
    log('✓ All radio change handlers setup complete');
  }

  handleVariantSelection(card) {
    log('handleVariantSelection called', card.dataset);
    
    this.variantCards.forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    const radio = card.querySelector('input[type="radio"]');
    if (radio && !radio.checked) radio.checked = true;

    const { variantId, variantPriceFormatted, variantShellPriceFormatted, variantPrice, variantCompareAtPrice, variantImageId, variantImageSrc } = card.dataset;
    this.basePrice = parseInt(variantPrice, 10);
    this.selectedVariant = { id: variantId, imageId: variantImageId, imageSrc: variantImageSrc };

    log('Price data:', { variantPrice, variantPriceFormatted, variantCompareAtPrice });

    // Update main price
    if (this.priceDisplay && variantPriceFormatted) {
      this.priceDisplay.textContent = variantPriceFormatted;
      log('Updated price display to:', variantPriceFormatted);
    }

    // Update compare-at price (it's already a formatted string)
    if (this.comparePriceDisplay) {
      if (variantCompareAtPrice) {
        this.comparePriceDisplay.textContent = variantCompareAtPrice;
        this.comparePriceDisplay.style.display = '';
        this.comparePriceDisplay.parentElement.style.display = '';
        log('Showing compare price:', variantCompareAtPrice);
      } else {
        this.comparePriceDisplay.style.display = 'none';
        if (this.comparePriceDisplay.parentElement) {
          this.comparePriceDisplay.parentElement.style.display = 'none';
        }
        log('Hiding compare price');
      }
    }

    // Update shell only price 
    if (this.shellPriceDisplay && variantShellPriceFormatted) {
      this.shellPriceDisplay.textContent = variantShellPriceFormatted;
    }

    if (variantImageId && variantImageSrc) {
      this.updateSliderForVariant(variantImageId, variantImageSrc);
    }

    if (variantId) {
      this.updateURL(variantId);
      this.dispatchVariantChange(variantId, this.basePrice);
    }
  }

  updateSliderForVariant(variantImageId, variantImageSrc) {
    const mainSwiperEl = this.step1Section?.querySelector('[data-product-main-swiper]');
    if (!mainSwiperEl) return;

    let attempts = 0;
    const maxAttempts = 60;
    
    const updateSlider = () => {
      if (mainSwiperEl.swiper) {
        const mainSwiper = mainSwiperEl.swiper;
        
        let targetSlideIndex = -1;
        mainSwiper.slides.forEach((slide, index) => {
          const img = slide.querySelector('[data-product-main-image]');
          if (img && img.dataset.mediaId === variantImageId) {
            targetSlideIndex = index;
          }
        });

        if (targetSlideIndex >= 0) {
          mainSwiper.slideTo(targetSlideIndex);
          
          const thumbnailsSwiperEl = this.step1Section?.querySelector('[data-product-thumbnails-swiper]');
          
          if (thumbnailsSwiperEl && thumbnailsSwiperEl.swiper) {
            const thumbnailsSwiper = thumbnailsSwiperEl.swiper;
            this.thumbnails.forEach(t => t.classList.remove('active'));
            if (this.thumbnails[targetSlideIndex]) {
              this.thumbnails[targetSlideIndex].classList.add('active');
            }
            thumbnailsSwiper.slideTo(targetSlideIndex);
          }
        } else {
          const firstSlide = mainSwiper.slides[0];
          if (firstSlide) {
            const firstImg = firstSlide.querySelector('[data-product-main-image]');
            if (firstImg) {
              firstImg.src = variantImageSrc;
              firstImg.dataset.mediaId = variantImageId;
              firstImg.dataset.mediaSrc = variantImageSrc;
              this.mainImage = firstImg;
              mainSwiper.slideTo(0);
              
              const thumbnailsSwiperEl = this.step1Section?.querySelector('[data-product-thumbnails-swiper]');
              if (thumbnailsSwiperEl && thumbnailsSwiperEl.swiper) {
                const thumbnailsSwiper = thumbnailsSwiperEl.swiper;
                this.thumbnails.forEach(t => t.classList.remove('active'));
                if (this.thumbnails[0]) {
                  this.thumbnails[0].classList.add('active');
                }
                thumbnailsSwiper.slideTo(0);
              }
            }
          }
        }
      } else if (attempts < maxAttempts) {
        attempts++;
        requestAnimationFrame(updateSlider);
      }
    };

    updateSlider();
  }

  setupDesignOwnButton() {
    if (!this.designOwnBtn) {
      const buttons = $$('button, a', this.step1Section);
      buttons.forEach(btn => {
        if (btn.textContent.toLowerCase().includes('design your own')) {
          this.designOwnBtn = btn;
        }
      });
    }

    if (this.designOwnBtn) {
      this.designOwnBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hideHeader();
        this.showStep2();
      });
    }
  }

  hideHeader() {
    const topbar = document.querySelector('[data-top-bar], .topbar');
    if (topbar) {
      // Add transition for smooth animation with longer delay
      topbar.style.setProperty('transition', 'opacity 0.5s ease-out, transform 0.5s ease-out, max-height 0.5s ease-out', 'important');
      topbar.style.setProperty('opacity', '0', 'important');
      topbar.style.setProperty('transform', 'translateY(-100%)', 'important');
      topbar.style.setProperty('max-height', '0', 'important');
      topbar.style.setProperty('overflow', 'hidden', 'important');
      // Hide completely after animation
      setTimeout(() => {
        topbar.style.setProperty('display', 'none', 'important');
      }, 500);
    }
  }

  showHeader() {
    const topbar = document.querySelector('[data-top-bar], .topbar');
    if (topbar) {
      // Show element first
      topbar.style.setProperty('display', '', 'important');
      // Get original height for smooth transition
      const originalHeight = topbar.scrollHeight;
      topbar.style.setProperty('max-height', originalHeight + 'px', 'important');
      // Add transition for smooth animation with longer delay
      topbar.style.setProperty('transition', 'opacity 0.5s ease-in, transform 0.5s ease-in, max-height 0.5s ease-in', 'important');
      // Trigger reflow to ensure initial state is applied
      void topbar.offsetHeight;
      // Animate in
      requestAnimationFrame(() => {
        topbar.style.setProperty('opacity', '1', 'important');
        topbar.style.setProperty('transform', 'translateY(0)', 'important');
        topbar.style.setProperty('overflow', '', 'important');
      });
    }
  }

  setupThumbnailSwitching() {
    const mainSwiperEl = this.step1Section?.querySelector('[data-product-main-swiper]');
    const thumbnailsSwiperEl = this.step1Section?.querySelector('[data-product-thumbnails-swiper]');
    
    if (!mainSwiperEl || !thumbnailsSwiperEl) {
      if (!this.thumbnails.length || !this.mainImage) return;
      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          this.thumbnails.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
          const newSrc = thumb.dataset.mediaSrc;
          if (this.mainImage && newSrc) this.mainImage.src = newSrc;
        });
      });
      return;
    }

    let attempts = 0;
    const maxAttempts = 60;
    
    const initializeSwipers = () => {
      if (mainSwiperEl.swiper && thumbnailsSwiperEl.swiper) {
        const mainSwiper = mainSwiperEl.swiper;
        const thumbnailsSwiper = thumbnailsSwiperEl.swiper;

        this.thumbnails.forEach((thumb, index) => {
          thumb.addEventListener('click', () => {
            this.thumbnails.forEach(t => t.classList.remove('active'));
            thumb.classList.add('active');
            const slideIndex = parseInt(thumb.dataset.slideIndex || index, 10);
            mainSwiper.slideTo(slideIndex);
            thumbnailsSwiper.slideTo(index);
            const mainSlide = mainSwiper.slides[slideIndex];
            const mainImg = mainSlide?.querySelector('[data-product-main-image]');
            if (mainImg) {
              this.mainImage = mainImg;
            }
          });
        });

        mainSwiper.on('slideChange', () => {
          const activeIndex = mainSwiper.activeIndex;
          this.thumbnails.forEach((thumb, index) => {
            const slideIndex = parseInt(thumb.dataset.slideIndex || index, 10);
            if (slideIndex === activeIndex) {
              this.thumbnails.forEach(t => t.classList.remove('active'));
              thumb.classList.add('active');
              thumbnailsSwiper.slideTo(index);
            }
          });
          const activeSlide = mainSwiper.slides[activeIndex];
          const mainImg = activeSlide?.querySelector('[data-product-main-image]');
          if (mainImg) {
            this.mainImage = mainImg;
          }
        });

        thumbnailsSwiper.on('slideChange', () => {
          const activeThumbIndex = thumbnailsSwiper.activeIndex;
          const thumb = this.thumbnails[activeThumbIndex];
          if (thumb) {
            const slideIndex = parseInt(thumb.dataset.slideIndex || activeThumbIndex, 10);
            mainSwiper.slideTo(slideIndex);
          }
        });
      } else if (attempts < maxAttempts) {
        attempts++;
        requestAnimationFrame(initializeSwipers);
      }
    };

    initializeSwipers();
  }

  // ============================================================================
  // STEP 2: CONFIGURATOR
  // ============================================================================

  async showStep2() {
    const selectedVariantId = this.getSelectedVariantId();
    if (!selectedVariantId) return;

    this.showLoadingState();

    try {
      const sectionHTML = await this.fetchStep2Section(selectedVariantId);
      if (sectionHTML) {
        this.updateStep2Content(sectionHTML);
        document.body.classList.add('portland-configurator-active');
        this.toggleStep2HiddenSections(false); // Hide extra sections in step 2
        this.setupStep2();
        this.loadStep2Data();
        this.hideLoadingState();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        ConfiguratorShared.StepHistoryManager.pushStep2(this);
      } else {
        this.hideLoadingState();
      }
    } catch (error) {
      console.error('Error loading Step 2:', error);
      this.hideLoadingState();
    }
  }

  /**
   * Toggle visibility of sections that should be hidden in step 2
   * @param {boolean} show - true to show sections, false to hide
   */
  toggleStep2HiddenSections(show) {
    const footerForm = document.querySelector('div.newsletter-wrapper');
    
    if (footerForm) {
      footerForm.style.display = show ? '' : 'none';
    }
    
    const mainContent = document.getElementById('MainContent');
    if (!mainContent) return;

    // Find all Shopify sections
    const allSections = mainContent.querySelectorAll('[id^="shopify-section-"]');
    
    allSections.forEach(section => {
      const sectionId = section.id;
      
      // Only keep step 2 sections visible, hide everything else
      // Exclude FAQ section - it's controlled separately by toggleFaqSection
      const isStep2Section = sectionId.includes('portland_step_2') || sectionId.includes('portland-step-2');
      const isFaqSection = sectionId.includes('global_modal_faq') || sectionId.includes('global-modal-faq');
      
      if (!isStep2Section && !isFaqSection) {
        section.style.display = show ? '' : 'none';
      }
    });
  }

  async fetchStep2Section(variantId) {
    let sectionId = this.step2Section?.dataset?.sectionId;
    if (!sectionId) {
      const wrapper = this.step2Section?.closest('[id^="shopify-section-"]');
      sectionId = wrapper?.id?.replace('shopify-section-', '') || 'portland_step_2';
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.text();
  }

  updateStep2Content(html) {
    if (!html || !this.step2Section) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const newSection = doc.querySelector('[data-configurator-step="2"]') ||
                       doc.querySelector('[data-portland-step-2]') ||
                       doc.querySelector('.portland-step-2') ||
                       doc.querySelector('section');

    if (newSection) {
      this.step2Section.innerHTML = newSection.innerHTML;
      if (newSection.dataset.sectionId) {
        this.step2Section.dataset.sectionId = newSection.dataset.sectionId;
      }
      this.step2Section.style.display = 'block';
    } else {
      const bodyContent = doc.body.innerHTML;
      if (bodyContent) {
        this.step2Section.innerHTML = bodyContent;
        this.step2Section.style.display = 'block';
      }
    }
  }

  showLoadingState() {
    if (this.designOwnBtn) {
      this.designOwnBtn.dataset.originalText = this.designOwnBtn.textContent;
      this.designOwnBtn.textContent = 'Loading...';
      this.designOwnBtn.disabled = true;
    }
  }

  hideLoadingState() {
    if (this.designOwnBtn) {
      this.designOwnBtn.textContent = this.designOwnBtn.dataset.originalText || 'Design Your Own';
      this.designOwnBtn.disabled = false;
    }
  }

  hideStep2({ fromPopstate = false } = {}) {
    if (!fromPopstate) {
      ConfiguratorShared.StepHistoryManager.exitStep2();
      return;
    }

    document.body.classList.remove('portland-configurator-active');
    this.toggleStep2HiddenSections(true); // Show extra sections when leaving step 2
    if (this.viewer3D) {
      this.viewer3D.dispose();
      this.viewer3D = null;
    }
    // Restore layout to 8-4 columns before hiding
    this.restoreDefaultLayout();

    // Remove product-summary-tab class when closing configurator (Portland-specific)
    const configuratorMain = $('[data-configurator-main]', this.step2Section);
    if (configuratorMain) {
      configuratorMain.classList.remove('product-summary-tab');
    }

    if (this.step2Section) this.step2Section.style.display = 'none';
    this.hideLoadingState();
    this.showHeader();
    // Hide FAQ section and footer when leaving configurator
    this.toggleFaqSection(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Show or hide the FAQ section, modal footer, and main site footer
   * @param {boolean} show - Whether to show (true) or hide (false) the FAQ elements
   */
  toggleFaqSection(show) {
    // Find the Shopify section wrapper containing the FAQ
    const faqSectionWrapper = document.querySelector('[id^="shopify-section-"][id*="global_modal_faq"], [id^="shopify-section-"][id*="global-modal-faq"]');
    const faqSection = document.querySelector('.global-modal-faq-section');
    const faqFooter = document.querySelector('.modal-faq-footer');
    const siteFooter = document.querySelector('.site-footer');
    
    if (show) {
      // Use !important to override CSS hiding rule
      if (faqSectionWrapper) faqSectionWrapper.style.setProperty('display', 'block', 'important');
      if (faqSection) faqSection.style.setProperty('display', 'block', 'important');
      if (faqFooter) faqFooter.style.setProperty('display', 'block', 'important');
      // Hide main site footer on Summary step
      if (siteFooter) siteFooter.style.setProperty('display', 'none', 'important');
    } else {
      // Let CSS handle hiding, just remove inline style
      if (faqSectionWrapper) faqSectionWrapper.style.removeProperty('display');
      if (faqSection) faqSection.style.removeProperty('display');
      if (faqFooter) faqFooter.style.removeProperty('display');
      // Show main site footer when not on Summary
      if (siteFooter) siteFooter.style.removeProperty('display');
    }
  }

  setupStep2() {
    if (!this.step2Section) return;

    if (this.step2ClickHandler) {
      this.step2Section.removeEventListener('click', this.step2ClickHandler);
    }

    this.step2ClickHandler = (e) => {
      // Click on configurator title to go back to step 1
      if (e.target.closest('[data-configurator-title]')) {
        e.preventDefault();
        this.hideStep2();
        return;
      }

      const tab = e.target.closest('[data-tab-btn], .tab-btn');
      if (tab?.dataset.tab) {
        // Prevent navigation to disabled tabs (e.g., Summary when form not filled)
        if (tab.disabled || tab.classList.contains('disabled')) {
          e.preventDefault();
          return;
        }
        this.switchTab(tab.dataset.tab);
        return;
      }

      if (e.target.closest('[data-back-step]')) {
        e.preventDefault();
        this.goToPreviousTab();
        return;
      }

      if (e.target.closest('[data-next-step]')) {
        e.preventDefault();
        this.goToNextTab();
        return;
      }

      // Handle Save Design button click
      const saveDesignBtn = e.target.closest('[data-save-design]');
      if (saveDesignBtn) {
        e.preventDefault();
        
        // Handle Start Over button click
        if (saveDesignBtn?.dataset.startOver === 'true') {
          this.handleStartOver();
          return;
        }
        
        // Get current tab
        const currentTab = q('[data-tab-btn].active', '.tab-btn.active', this.step2Section)?.dataset.tab;
        
        // If already on services tab, try to submit the form
        if (currentTab === 'services') {
          if (!this.hasValidUserFormData()) {
            // Try to submit the form programmatically
            const form = $('[data-summary-form]', this.step2Section);
            if (form) {
              if (form.checkValidity()) {
                // Form is valid - submit it
                form.requestSubmit();
              } else {
                // Form is invalid - show validation messages and focus first invalid field
                form.reportValidity();
                this.focusSaveDesignForm();
              }
            } else {
              this.focusSaveDesignForm();
            }
          } else {
            // Form already submitted, go to summary
            this.switchTab('summary');
          }
          return;
        }
        
        // If not on services tab, redirect to services tab
        this.switchTab('services');
        
        // Scroll to form or installation options after a short delay to ensure content is rendered
        setTimeout(() => {
          const form = $('[data-summary-form]', this.step2Section);
          if (form && form.offsetParent !== null) {
            // Form is visible, focus on it
            this.focusSaveDesignForm();
          } else {
            // Form not visible yet (user needs to select installation first), focus on installation options
            this.focusInstallationOptions();
          }
        }, 150);
        
        return;
      }
    };

    this.step2Section.addEventListener('click', this.step2ClickHandler);
  }

  // ============================================================================
  // DATA LOADING & INITIALIZATION
  // ============================================================================

  loadStep2Data() {
    const modelConfigEl = $('[data-model-config]', this.step2Section);
    const variantFallbackEl = $('[data-variant-fallback]', this.step2Section);
    const sectionSettingsEl = $('[data-section-settings]', this.step2Section);

    let modelConfig = null, variantFallback = null, sectionSettings = null;

    try {
      if (modelConfigEl?.textContent.trim()) {
        const rawData = modelConfigEl.textContent.trim();
        modelConfig = rawData.startsWith('"') && rawData.endsWith('"')
          ? JSON.parse(JSON.parse(rawData))
          : JSON.parse(rawData);
      }
    } catch (e) { console.error('Error parsing model config:', e); }

    try {
      if (variantFallbackEl?.textContent.trim()) {
        variantFallback = JSON.parse(variantFallbackEl.textContent);
      }
    } catch (e) { console.error('Error parsing variant fallback:', e); }

    try {
      if (sectionSettingsEl?.textContent.trim()) {
        sectionSettings = JSON.parse(sectionSettingsEl.textContent);
      }
    } catch (e) { console.error('Error parsing section settings:', e); }

    this.modelConfig = {
      ...(modelConfig || {}),
      ...(sectionSettings || {}),
      services: {
        ...(modelConfig?.services || {}),
        ...(sectionSettings?.services || {})
      }
    };
    
    // Detect model structure type
    const isPanelBased = modelConfig?.loadingSequence?.length > 0;
    const isOrientationBased = modelConfig?.modelFiles || modelConfig?.models;
    
    log('Model structure:', {
      isPanelBased,
      isOrientationBased,
      loadingSequence: modelConfig?.loadingSequence?.length || 0,
      modelFiles: !!modelConfig?.modelFiles
    });
    
    // Normalize steps - support both formats
    if (modelConfig?.steps) {
      // Check if steps have 'sections' (panel-based) or 'options' (orientation-based)
      const firstStep = modelConfig.steps[0];
      if (firstStep?.sections) {
        // Panel-based format (Signature/Portland Series)
        this.steps = modelConfig.steps.map(step => ({
          step: step.step,
          id: step.id,
          name: step.title || step.id,
          description: step.description,
          sections: step.sections || []
        }));
      } else {
        // Orientation-based format (legacy Portland)
        this.steps = modelConfig.steps.map(step => {
          const normalized = { ...step };
          if (normalized.options) {
            Object.keys(normalized.options).forEach(key => {
              const opt = normalized.options[key];
              if (opt.choices && !opt.options) {
                normalized.options[key] = { ...opt, options: opt.choices };
              }
            });
          }
          return normalized;
        });
      }
    } else {
      this.steps = [];
    }

    // Extract model data - support both formats
    if (isPanelBased) {
      // Panel-based model data
      const dims = modelConfig?.model?.dimensions || {};
      
      // For panel-based models like Portland:
      // - dims.width = building width (e.g., 8) -> API depth
      // - dims.depth = building depth (e.g., 10) -> API length
      const apiDepth = dims.width || modelConfig?.depth;
      const apiLength = dims.depth || modelConfig?.length;
      
      this.modelsData = {
        id: modelConfig?.model?.id,
        name: modelConfig?.model?.name || modelConfig?.name,
        size: modelConfig?.model?.size || modelConfig?.size,
        depth: apiDepth,
        length: apiLength,
        squareFootage: dims.squareFeet || modelConfig?.squareFootage,
        basePrice: modelConfig?.model?.basePrice || modelConfig?.basePrice,
        description: modelConfig?.model?.description || modelConfig?.description,
        rawDimensions: dims // Store raw dimensions for debugging
      };
      
      log('[PortlandModal] Panel-based modelsData extracted:', {
        depth: apiDepth,
        length: apiLength,
        rawDimensions: dims
      });
    } else {
      // Orientation-based model data
      const topLevelModelFiles = modelConfig?.modelFiles;
      this.modelsData = topLevelModelFiles
        ? { name: modelConfig?.name || 'portland-1', size: modelConfig?.size, depth: modelConfig?.depth, length: modelConfig?.length, squareFootage: modelConfig?.squareFootage, basePrice: modelConfig?.basePrice, description: modelConfig?.description, modelFiles: topLevelModelFiles }
        : (modelConfig?.models || {});
    }

    this.texturesData = modelConfig?.textures || {};
    this.colorsData = modelConfig?.colors || {};
    this.meshGroupsData = modelConfig?.meshGroups || {};
    this.defaultConfig = modelConfig?.defaultConfiguration || {};

    const variantInfo = modelConfig?.variantInfo || variantFallback || {};
    this.basePrice = this.modelsData?.basePrice || modelConfig?.model?.basePrice || variantInfo.price || variantFallback?.price || 0;
    
    // Store variant documents for downloads section
    this.variantDocuments = variantFallback?.documents || [];
    
    // Re-initialize drawers on the new AJAX-loaded DOM
    this.setupPermitPlansDrawer();
    this.setupFoundationTypeDrawer();

    this.updateStep2Header(variantInfo);
    this.initializeDefaultConfig();
    this.generateTabs();
    // Disable Summary tab by default until form is submitted
    this.updateSummaryTabState();
    
    // CRITICAL: Set currentStep BEFORE populating and initializing 3D viewer
    this.currentStep = 1;
    log('Initial step set to 1 (Interior view)');
    
    this.populateStepOptions(1);

    // Initialize 3D viewer based on model type
    if (isPanelBased || isOrientationBased) {
      requestAnimationFrame(() => this.init3DViewer());
    }
  }

  initializeDefaultConfig() {
    this.currentConfig = { ...this.defaultConfig };
    this.currentStep = 1;
  }

  // ============================================================================
  // TABS & NAVIGATION
  // ============================================================================

  generateTabs() {
    const tabsContainer = $('[data-configurator-tabs]', this.step2Section);
    if (!tabsContainer || !this.steps.length) return;

    tabsContainer.innerHTML = '';

    this.steps.forEach((step, index) => {
      tabsContainer.appendChild(this.createTabButton(step.name, step.step, index === 0));
    });

    tabsContainer.appendChild(this.createTabButton('Services', this.steps.length + 1, false));
    tabsContainer.appendChild(this.createTabButton('Summary', this.steps.length + 2, false));
  }

  createTabButton(name, step, isActive) {
    const btn = document.createElement('button');
    btn.className = `tab-btn${isActive ? ' active' : ''}`;
    btn.setAttribute('data-tab-btn', '');
    btn.dataset.tab = name.toLowerCase();
    btn.dataset.step = step;
    btn.textContent = name;
    return btn;
  }

  /**
   * Enable or disable the Summary tab based on form completion state.
   * Disabled tabs cannot be clicked and show visual feedback.
   */
  updateSummaryTabState() {
    const summaryTab = q('[data-tab-btn][data-tab="summary"]', '.tab-btn[data-tab="summary"]', this.step2Section);
    if (!summaryTab) return;

    const isEnabled = this.hasValidUserFormData();
    summaryTab.disabled = !isEnabled;
    summaryTab.classList.toggle('disabled', !isEnabled);
    summaryTab.setAttribute('aria-disabled', String(!isEnabled));
    
    if (!isEnabled) {
      summaryTab.title = 'Complete the installation form to view summary';
    } else {
      summaryTab.removeAttribute('title');
    }
  }

  switchTab(tabName) {
    const step = this.steps.find(s => s.name.toLowerCase() === tabName.toLowerCase());
    const stepNumber = step?.step;
    const tabLower = tabName.toLowerCase();

    // Restore default 8-4 layout when NOT switching to summary (summary uses 6-6 layout)
    if (tabLower !== 'summary') {
      this.restoreDefaultLayout();
    }

    qa('[data-tab-btn]', '.tab-btn', this.step2Section).forEach(tab => {
      tab.classList.toggle('active', tab.dataset.tab === tabLower);
    });

    // Show FAQ section and footer only on summary page
    this.toggleFaqSection(tabLower === 'summary');

    // Add/remove product-summary-tab class based on current tab (Portland-specific)
    const configuratorMain = $('[data-configurator-main]', this.step2Section);
    if (configuratorMain) {
      if (tabLower === 'summary') {
        configuratorMain.classList.add('product-summary-tab');
      } else {
        configuratorMain.classList.remove('product-summary-tab');
      }
    }

    if (tabLower !== 'summary') {
      const panelFooter = q('[data-panel-footer]', '.panel-footer', this.step2Section);
      if (panelFooter) panelFooter.style.display = '';

      const saveDesignBtn = $('[data-save-design]', this.step2Section);
      if (saveDesignBtn?.dataset.startOver === 'true') {
        saveDesignBtn.textContent = 'SAVE DESIGN';
        delete saveDesignBtn.dataset.startOver;
      }

      // Show header Save Design button when not on services form (will be hidden again in populateServicesOptions if needed)
      if (tabLower !== 'services' && saveDesignBtn) {
        saveDesignBtn.style.display = '';
      }

      // Pass skipQueueUpdate=true to prevent API calls on tab switch - API should only be called on Services step form submission
      this.updateTotalPrice(true);
    }

    if (stepNumber) {
      this.populateStepOptions(stepNumber);
      this.update3DViewForStep(tabLower);
    } else if (tabLower === 'services') {
      this.populateServicesOptions();
      this.viewer3D?.setViewForStep(2);
    } else if (tabLower === 'summary') {
      this.showSummary();
      this.viewer3D?.setViewForStep(5);
    }

    // Scroll to top when switching tabs
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  update3DViewForStep(stepName) {
    if (!this.viewer3D) return;
    
    const stepLower = stepName.toLowerCase().replace(/\s+/g, '-');
    
    log(`🎬 update3DViewForStep called for: ${stepName}`);
    
    // Determine view type based on step/tab name
    const isInteriorView = stepLower.includes('interior') || stepLower.includes('choose-your-interior');
    const isElevationView = stepLower.includes('elevation') || stepLower.includes('door') || stepLower.includes('window');
    const isExteriorView = stepLower.includes('exterior') || stepLower.includes('siding') || stepLower.includes('color');
    
    if (isInteriorView) {
      // STEP 1: INTERIOR VIEW
      // - Top-down camera
      // - Roof HIDDEN (to see inside)
      log('  → Interior view: top-down, roof hidden');
      this.viewer3D.setViewForStep(1);
    } else if (isElevationView) {
      // STEP 2: ELEVATION VIEW (Doors & Windows)
      // - Front exterior camera
      // - Roof VISIBLE
      log('  → Elevation view: front exterior, roof visible');
      this.viewer3D.setViewForStep(2);
      
      // Auto-rotate to front panel when entering this tab
      if (this.viewer3D.setViewForPanel) {
        setTimeout(() => {
          log('  → Auto-rotating to Front Panel');
          this.viewer3D.setViewForPanel('frontPanel');
        }, 100);
      }
    } else if (isExteriorView) {
      // STEP 3: EXTERIOR VIEW (Siding & Colors)
      // - Front exterior camera
      // - Roof VISIBLE
      log('  → Exterior view: front exterior, roof visible');
      this.viewer3D.setViewForStep(2);
    } else {
      // Default: Exterior view
      log(`  → Default view for ${stepName}: exterior front view`);
      this.viewer3D.setViewForStep(2);
    }
  }

  goToNextTab() {
    const currentTab = q('[data-tab-btn].active', '.tab-btn.active', this.step2Section)?.dataset.tab;

    if (currentTab === 'services') {
      if (!this.hasValidUserFormData()) {
        // Try to submit the form programmatically when clicking SAVE DESIGN button
        const form = $('[data-summary-form]', this.step2Section);
        if (form) {
          if (form.checkValidity()) {
            // Form is valid - submit it
            form.requestSubmit();
          } else {
            // Form is invalid - show validation messages and focus first invalid field
            form.reportValidity();
            this.focusSaveDesignForm();
          }
        } else {
          this.focusSaveDesignForm();
        }
        return;
      }
      this.switchTab('summary');
      return;
    }

    const nextStep = this.steps.find(s => s.step === this.currentStep + 1);
    this.switchTab(nextStep?.name || 'services');
  }

  goToPreviousTab() {
    const currentTab = q('[data-tab-btn].active', '.tab-btn.active', this.step2Section)?.dataset.tab;

    if (currentTab === 'summary') {
      this.switchTab('services');
      return;
    }

    if (currentTab === 'services') {
      const lastStep = this.steps[this.steps.length - 1];
      if (lastStep) this.switchTab(lastStep.name);
      return;
    }

    if (this.currentStep > 1) {
      const prevStep = this.steps.find(s => s.step === this.currentStep - 1);
      if (prevStep) this.switchTab(prevStep.name);
    }
  }

  // ============================================================================
  // STEP OPTIONS RENDERING
  // ============================================================================

  populateStepOptions(stepNumber) {
    const step = this.steps.find(s => s.step === stepNumber);
    if (!step) return;

    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    contentWrapper.innerHTML = '';

    const panelContent = document.createElement('div');
    panelContent.className = 'panel-content';
    panelContent.dataset.tabContent = (step.name || step.id).toLowerCase().replace(/\s+/g, '-');

    const sectionsContainer = document.createElement('div');
    sectionsContainer.className = 'config-sections-container';
    sectionsContainer.dataset.configSections = '';

    const stepNameLower = (step.name || step.id || '').toLowerCase();

    // PANEL-BASED MODEL (has sections array) - appears FIRST
    if (step.sections && Array.isArray(step.sections)) {
      log('Rendering panel-based step:', step.name, 'with', step.sections.length, 'sections');
      step.sections.forEach(section => {
        // Skip sections with hide: true (unconditionally hidden)
        if (section.hide === true || section.hide === 'true') {
          return;
        }
        // Check showWhen conditions
        if (section.showWhen && !this.checkShowWhen(section.showWhen)) {
          return;
        }
        if (section.hidden && !this.checkShowWhen(section.showWhen)) {
          return;
        }
        
        sectionsContainer.appendChild(this.createPanelBasedSection(section));
      });
    }
    // ORIENTATION-BASED MODEL (has options object) - appears FIRST
    else if (step.options) {
      log('Rendering orientation-based step:', step.name);
      const useGrouping = ['interior', 'finishes'].includes(stepNameLower);

      if (useGrouping) {
        this.renderGroupedSections(step.options, sectionsContainer);
      } else {
        this.renderFlatSections(step.options, sectionsContainer);
      }
    }

    // Add Lifestyle interior package info AFTER config sections
    const isInteriorStep = stepNameLower.includes('interior');
    if (isInteriorStep && this.modelConfig?.interiorPackageInfo?.show && this.modelConfig.interiorPackageInfo.items) {
      const infoDiv = document.createElement('div');
      infoDiv.className = 'portland-interior-package-info';
      infoDiv.innerHTML = this.modelConfig.interiorPackageInfo.items;
      sectionsContainer.appendChild(infoDiv);
    }

    panelContent.appendChild(sectionsContainer);
    contentWrapper.appendChild(panelContent);
    this.updateNextButtonText(stepNumber);
    this.currentStep = stepNumber;
    // Pass skipQueueUpdate=true to prevent API calls on tab switch
    this.updateTotalPrice(true);
  }

  /**
   * Check showWhen conditions for conditional sections
   */
  checkShowWhen(showWhen) {
    if (!showWhen) return true;
    
    for (const [key, allowedValues] of Object.entries(showWhen)) {
      const currentValue = this.currentConfig[key];
      if (Array.isArray(allowedValues)) {
        if (!allowedValues.includes(currentValue)) return false;
      } else {
        if (currentValue !== allowedValues) return false;
      }
    }
    return true;
  }

  /**
   * Create a section element for panel-based models
   * HANDLES CONDITIONAL VISIBILITY with showWhen
   */
  createPanelBasedSection(section) {
    // CHECK UNCONDITIONAL HIDE FIRST
    // If section has "hide": true, skip rendering entirely (no conditions)
    if (section.hide === true || section.hide === 'true') {
      return document.createDocumentFragment(); // Return empty fragment
    }
    
    // CHECK CONDITIONAL VISIBILITY
    // If section has "hidden": true and "showWhen" conditions, check if they're met
    if (section.hidden === true || section.hidden === 'true') {
      const conditionsMet = this.checkShowWhen(section.showWhen);
      if (!conditionsMet) {
        log(`Section ${section.id} hidden - conditions not met:`, section.showWhen);
        return document.createDocumentFragment(); // Return empty fragment
      } else {
        log(`Section ${section.id} shown - conditions met:`, section.showWhen);
      }
    }
    
    // SPECIAL CASE: Hide sidingColor section when cedar siding is selected
    if (section.id === 'sidingColor') {
      console.log('[PortlandModal] ===== createPanelBasedSection: sidingColor =====');
      const currentSiding = String(this.currentConfig?.siding || '').toLowerCase().trim();
      console.log('[PortlandModal] section.id:', section.id);
      console.log('[PortlandModal] this.currentConfig?.siding:', this.currentConfig?.siding);
      console.log('[PortlandModal] currentSiding (normalized):', currentSiding);
      console.log('[PortlandModal] this.currentConfig:', JSON.stringify(this.currentConfig));
      
      // More comprehensive cedar detection - check for cedar in any form
      const isCedarSiding = currentSiding.includes('cedar') || 
                           currentSiding === 'horiz_plank' || 
                           currentSiding === 'cedar-plank' || 
                           currentSiding === 'cedar-shake' ||
                           currentSiding === 'cedar_shake' ||
                           currentSiding === 'cedarplank' ||
                           currentSiding === 'cedarshake' ||
                           currentSiding.replace(/[\s_-]/g, '').includes('cedar');
      
      console.log('[PortlandModal] isCedarSiding:', isCedarSiding);
      
      if (isCedarSiding) {
        console.log('[PortlandModal] ✓ createPanelBasedSection: Hiding sidingColor section - cedar siding selected:', currentSiding);
        return document.createDocumentFragment(); // Return empty fragment
      } else {
        console.log('[PortlandModal] ✓ createPanelBasedSection: Showing sidingColor section (not cedar):', currentSiding);
      }
      console.log('[PortlandModal] ===============================================');
    }
    
    const sectionEl = document.createElement('div');
    sectionEl.className = 'config-section mb-6';
    sectionEl.dataset.configSection = section.id;
    
    // Store showWhen data for dynamic updates
    if (section.showWhen) {
      sectionEl.dataset.showWhen = JSON.stringify(section.showWhen);
    }
    if (section.hidden) {
      sectionEl.dataset.conditionalSection = 'true';
    }
    
    // Add click handler to rotate camera when section is focused (for panel sections)
    const isPanelSection = ['frontPanel', 'backPanel', 'leftPanel', 'rightPanel', 'roof'].includes(section.id);
    if (isPanelSection) {
      sectionEl.style.cursor = 'pointer';
      sectionEl.addEventListener('click', (e) => {
        // Only rotate if not clicking an option card
        if (!e.target.closest('[data-option-card]')) {
          log('Section clicked, rotating to panel:', section.id);
          if (this.viewer3D?.setViewForPanel) {
            this.viewer3D.setViewForPanel(section.id);
          }
        }
      });
    }

    const title = document.createElement('div');
    title.className = 'config-title d-flex align-items-center gap-1 mb-2';
    title.dataset.configTitle = section.id;
    
    const isPackageSection = section.options?.some(opt => {
      const name = String(opt.displayName || opt.value || '').toLowerCase().replace(/[\s_-]/g, '');
      return name.includes('shellonly') || name.includes('lifestyleinterior') || name.includes('fullyequip');
    });
    if (isPackageSection) {
      section.options.sort((a, b) => {
        const getScore = (opt) => {
          const name = String(opt.displayName || opt.value || '').toLowerCase().replace(/[\s_-]/g, '');
          if (name.includes('fullyequip')) return 1;
          if (name.includes('lifestyleinterior')) return 1;
          if (name.includes('shellonly')) return 2;
          return 3;
        };
        return getScore(a) - getScore(b);
      });
      // Force default assignment to the top-ranked package if not manually selected by user
      if (typeof this.hasForcedPackageDefault === 'undefined') {
        this.currentConfig[section.id] = section.options[0].value;
        this.hasForcedPackageDefault = true;
      }
    }
    
    const defaultValue = this.currentConfig[section.id];
    const selectedOption = section.options?.find(opt => opt.value === defaultValue) || section.options?.[0];
    
    // Set default if not set
    if (!defaultValue && selectedOption) {
      this.currentConfig[section.id] = selectedOption.value;
    }
    
    title.innerHTML = `<p class="config-title-label fs-sm fw-medium text-black mb-0" style="letter-spacing: -0.01em;">${section.title}:</p> <span class="config-title-value fs-sm" style="letter-spacing: -0.01em;">${selectedOption?.displayName || ''}</span>`;
    sectionEl.appendChild(title);

    if (section.description) {
      const desc = document.createElement('p');
      desc.className = 'config-section-desc d-none';
      desc.textContent = section.description;
      sectionEl.appendChild(desc);
    }
    
    // Add helpful hint for panel sections
    if (isPanelSection) {
      const hint = document.createElement('p');
      hint.className = 'config-section-hint d-none';
      hint.textContent = 'Click section title to rotate 3D view';
      sectionEl.appendChild(hint);
    }

    // Create options container
    const optionsContainer = this.createPanelOptionsContainer(section);
    this.populatePanelOptions(optionsContainer, section, defaultValue || selectedOption?.value);
    sectionEl.appendChild(optionsContainer);

    return sectionEl;
  }

  /**
   * Create options container for panel-based sections
   */
  createPanelOptionsContainer(section) {
    const container = document.createElement('div');
    const sectionId = section.id.toLowerCase();
    
    let className = 'config-options';
    
    if (section.options?.some(opt => opt.color?.hex)) {
      className += ' config-options--colors';
    } else if (section.options?.some(opt => opt.texture || opt.thumbnail)) {
      className += ' config-options--swatches';
    }

    container.className = className;
    container.dataset.configOptions = section.id;
    return container;
  }

  /**
   * Populate options for panel-based sections
   */
  populatePanelOptions(container, section, defaultValue) {
    if (!section.options?.length) return;

    const visibleOptions = section.options.filter(opt => !opt.hide);
    
    visibleOptions.forEach((option, index) => {
      const isSelected = option.value === defaultValue;
      container.appendChild(this.createPanelOptionCard(section.id, option, isSelected));
    });
  }

  /**
   * Create option card for panel-based sections
   */
  createPanelOptionCard(sectionId, option, isSelected) {
    const card = document.createElement('div');
    const isColor = option.color?.hex;
    const isTexture = option.thumbnail || option.texture;

    let classes = 'option-card';
    if (isSelected) classes += ' selected';
    if (isColor) classes += ' option-card--color';
    if (isTexture) classes += ' option-card--texture';

    card.className = classes;
    card.setAttribute('data-option-card', '');
    card.dataset.section = sectionId;
    card.dataset.value = option.value;
    card.dataset.price = option.price || 0;

    if (option.modelFile) card.dataset.modelFile = option.modelFile;
    if (option.color?.hex) card.dataset.hex = option.color.hex;
    if (option.texture) card.dataset.texture = option.texture;

    let html = `<span class="aspect-variant-check">
                  <svg viewBox="0 0 8 7" fill="none" style="width: 11px; height: 6px; margin-bottom: -1px;">
                    <path d="M0.5 3.35714L2.77027 5.5L7.5 0.5" stroke="currentColor" stroke-linecap="round"/>
                  </svg>
                </span>`;

    if (isColor) {
      html += `<div class="option-card-swatch w-100 h-100" style="background-color: ${option.color.hex}; transition: all .3s ease-in-out;"></div>`;
    } else if (isTexture) {
      html += `<div class="option-card-icon">
                <img src="${option.texture || option.thumbnail}" alt="${option.displayName}" title="${option.displayName}" loading="lazy" class="w-100 h-100 object-fit-contain">
              </div>`;
    }

    html += `<div class="option-card-content">
      <p class="option-card-label fw-medium mb-0 text-black">${option.displayName}</p>
      ${option.description ? `<p class="option-card-desc mb-0 fs-sm mt-1 text-gray-600 d-none">${option.description}</p>` : ''}
      ${option.price > 0
        ? `<span class="option-card-price text-gray-600 d-block">+${formatPrice(option.price)}</span>`
        : '<p class="option-card-included text-gray-600 mb-0">Included</p>'}
    </div>`;

    card.innerHTML = html;
    card.addEventListener('click', () => this.selectPanelOption(sectionId, card, option));
    return card;
  }

  /**
   * Select option for panel-based sections
   * HANDLES DEPENDENT SECTIONS (showWhen conditions) AND DYNAMIC SIDING COLORS
   */
  selectPanelOption(sectionId, card, option) {
    log('Option selected:', sectionId, option.value);
    
    // DEBUG: Log siding selection
    if (sectionId === 'siding') {
      console.log('[PortlandModal] ===== SIDING SELECTED (selectPanelOption) =====');
      console.log('[PortlandModal] option.value:', option.value);
      console.log('[PortlandModal] option.displayName:', option.displayName);
      console.log('[PortlandModal] this.currentConfig.siding:', this.currentConfig.siding);
      console.log('[PortlandModal] =========================');
    }
    
    const container = card.parentElement;
    qa('[data-option-card]', '.option-card', container).forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    const titleEl = $(`[data-config-title="${sectionId}"]`, this.step2Section);
    if (titleEl) {
      const valueEl = titleEl.querySelector('.config-title-value, [data-config-title-value]');
      if (valueEl) valueEl.textContent = option.displayName || option.value;
    }

    // Store previous value to detect changes
    const previousValue = this.currentConfig[sectionId];
    this.currentConfig[sectionId] = option.value;

    // STATE RESET: If form was already submitted and user changes options,
    // invalidate the form data (requires re-submission before accessing Summary).
    if (this.hasValidUserFormData()) {
      console.log('[Portland] selectPanelOption - Clearing form data due to option change');
      this.userFormData = null;
      window.studioShedUserData = null;
      this.finalPriceData = null;
      this._finalPriceDataZip = null;
      this.updateSummaryTabState();
    }
    
    // CRITICAL: Siding Style changes trigger 3D model update
    // The 3D update will apply the new texture with current color
    if (sectionId === 'siding' && previousValue !== option.value) {
      console.log('\n🎨 SIDING STYLE CHANGED IN UI');
      console.log('  From:', previousValue);
      console.log('  To:', option.value);
      console.log('  Current color:', this.currentConfig.sidingColor);
      
      // Option 1: If style has specific color options (nested in JSON), use those
      if (option.colorOptions && Array.isArray(option.colorOptions) && option.colorOptions.length > 0) {
        console.log(`  ✓ Found ${option.colorOptions.length} specific colors for ${option.value}`);
        console.log('  Updating sidingColor UI...');
        this.updateSidingColorOptionsWithStyleColors(option);
      } 
      // Option 2: All styles use same colors (Portland standard - 9 colors for all)
      else {
        console.log('  ✓ Using standard 9-color palette for all styles');
        console.log('  3D model will update with new texture + current color');
      }
    }
    
    // CHECK IF THIS CHANGE AFFECTS DEPENDENT SECTIONS
    if (previousValue !== option.value) {
      log(`Config changed: ${sectionId} from ${previousValue} to ${option.value}`);
      this.updateDependentSections();
    }
    
    // After re-rendering, ensure sidingColor section visibility is correct for cedar
    // This is needed because updateDependentSections() may re-render the step
    if (sectionId === 'siding') {
      console.log('[PortlandModal] ===== POST-RENDER CHECK =====');
      console.log('[PortlandModal] Checking sidingColor section visibility after re-render');
      console.log('[PortlandModal] option.value:', option.value);
      console.log('[PortlandModal] this.currentConfig.siding:', this.currentConfig.siding);
      
      const colorSectionEl = $('[data-config-section="sidingColor"]', this.step2Section);
      console.log('[PortlandModal] colorSectionEl found:', !!colorSectionEl);
      
      if (colorSectionEl) {
        const sidingValue = String(option.value || this.currentConfig.siding || '').toLowerCase().trim();
        console.log('[PortlandModal] sidingValue (normalized):', sidingValue);
        
        // More comprehensive cedar detection - check for cedar in any form
        const isCedarSiding = sidingValue.includes('cedar') || 
                             sidingValue === 'horiz_plank' || 
                             sidingValue === 'cedar-plank' || 
                             sidingValue === 'cedar-shake' ||
                             sidingValue === 'cedar_shake' ||
                             sidingValue === 'cedarplank' ||
                             sidingValue === 'cedarshake' ||
                             sidingValue.replace(/[\s_-]/g, '').includes('cedar');
        
        console.log('[PortlandModal] isCedarSiding:', isCedarSiding);
        
        if (isCedarSiding) {
          colorSectionEl.style.display = 'none';
          delete this.currentConfig.sidingColor;
          console.log('[PortlandModal] ✓ Post-render: Hid sidingColor section for cedar siding:', sidingValue);
        } else {
          colorSectionEl.style.display = '';
          console.log('[PortlandModal] ✓ Post-render: Showing sidingColor section (not cedar):', sidingValue);
        }
      } else {
        console.log('[PortlandModal] ⚠️ colorSectionEl not found in DOM after re-render');
      }
      console.log('[PortlandModal] ============================');
    }
    
    this.updateTotalPrice(true);
    
    // Rotate camera to panel FIRST (for panels with modelFile), then update model
    const isPanelSection = ['frontPanel', 'backPanel', 'leftPanel', 'rightPanel', 'roof'].includes(sectionId);
    if (isPanelSection && option.modelFile) {
      log('🎬 Panel option selected - rotating camera before model update');
      if (this.viewer3D?.setViewForPanel) {
        this.viewer3D.setViewForPanel(sectionId);
      }
    }
    
    this.update3DViewerPanelBased(sectionId, option);
  }

  /**
   * Highlight siding color section to show it's being updated
   * DISABLED: Yellow background highlight removed per user request
   */
  highlightSidingColorSection() {
    // Function disabled - no visual highlight
    return;
  }

  /**
   * Update sidingColor section options based on selected siding style
   * This handles cases where different siding styles have different available colors
   */
  updateSidingColorOptionsWithStyleColors(sidingStyleOption) {
    console.log('[PortlandModal] ===== updateSidingColorOptionsWithStyleColors =====');
    console.log('[PortlandModal] sidingStyleOption.value:', sidingStyleOption.value);
    console.log('[PortlandModal] sidingStyleOption.displayName:', sidingStyleOption.displayName);
    
    // Check if this is a cedar siding type (cedar doesn't have color options)
    const sidingValue = String(sidingStyleOption.value || '').toLowerCase().trim();
    console.log('[PortlandModal] sidingValue (normalized):', sidingValue);
    
    // More comprehensive cedar detection
    const isCedarSiding = sidingValue.includes('cedar') || 
                        sidingValue === 'horiz_plank' || 
                        sidingValue === 'cedar-plank' || 
                        sidingValue === 'cedar-shake' ||
                        sidingValue === 'cedar_shake' ||
                        sidingValue === 'cedarplank' ||
                        sidingValue === 'cedarshake' ||
                        sidingValue.replace(/[\s_-]/g, '').includes('cedar');
    
    console.log('[PortlandModal] isCedarSiding:', isCedarSiding);
    
    // Find the sidingColor section element in DOM
    const colorSectionEl = $('[data-config-section="sidingColor"]', this.step2Section);
    if (!colorSectionEl) {
      console.log('[PortlandModal] ⚠️ sidingColor section element not found in DOM');
      console.log('[PortlandModal] ===========================================');
      return;
    }
    
    // If cedar siding, hide the section and return early
    if (isCedarSiding) {
      console.log('[PortlandModal] Hiding sidingColor section - cedar siding selected');
      colorSectionEl.style.display = 'none';
      delete this.currentConfig.sidingColor;
      console.log('[PortlandModal] ✓ Cleared sidingColor from config (cedar siding selected)');
      console.log('[PortlandModal] ===========================================');
      return;
    }
    
    // Get color options - Priority: nested in style option > sidingColor section
    let colorOptions = sidingStyleOption.colorOptions;
    let colorSource = 'nested in siding style';
    
    // Fallback: Get from sidingColor section if not nested
    if (!colorOptions || colorOptions.length === 0) {
      const sidingColorSection = this.findSectionById('sidingColor');
      colorOptions = sidingColorSection?.options;
      colorSource = 'sidingColor section';
    }
    
    if (!colorOptions || colorOptions.length === 0) {
      console.log('[PortlandModal] ⚠️ No color options found from any source');
      console.log('[PortlandModal] ===========================================');
      return;
    }
    
    // Filter out hidden options
    colorOptions = colorOptions.filter(opt => !opt.hide);
    
    log(`[PortlandModal] Using ${colorOptions.length} visible colors from: ${colorSource}`);
    
    // Show the section if it was hidden
    colorSectionEl.style.display = '';

    // Find the options container
    const optionsContainer = $('.config-options', colorSectionEl);
    if (!optionsContainer) {
      log('[PortlandModal] Options container not found in sidingColor section');
      return;
    }

    // Clear existing options
    optionsContainer.innerHTML = '';
    log('[PortlandModal] Cleared existing color options');

    // Check if current selected color is still available in new options
    const currentColor = this.currentConfig.sidingColor;
    const isCurrentColorAvailable = colorOptions.some(opt => opt.value === currentColor);
    
    // If current color not available, reset to first option
    if (!isCurrentColorAvailable && colorOptions.length > 0) {
      const firstColor = colorOptions[0];
      this.currentConfig.sidingColor = firstColor.value;
      log(`[PortlandModal] Reset sidingColor to: ${firstColor.value}`);
      
      // Update the 3D model with new color
      this.update3DViewerPanelBased('sidingColor', firstColor);
    }

    // Render new color options
    log('[PortlandModal] Rendering color options:');
    colorOptions.forEach((colorOption, index) => {
      const isSelected = colorOption.value === this.currentConfig.sidingColor;
      log(`  ${index + 1}. ${colorOption.displayName} (${colorOption.color?.hex || colorOption.hex}) ${isSelected ? '✓ SELECTED' : ''}`);
      
      const card = this.createPanelOptionCard('sidingColor', colorOption, isSelected);
      optionsContainer.appendChild(card);
    });

    // Update the section title with current selection
    const titleEl = $('[data-config-title="sidingColor"]', this.step2Section);
    if (titleEl) {
      const selectedOption = colorOptions.find(opt => opt.value === this.currentConfig.sidingColor);
      const valueEl = titleEl.querySelector('.config-title-value, [data-config-title-value]');
      if (valueEl && selectedOption) {
        valueEl.textContent = selectedOption.displayName || selectedOption.value;
      }
    }
    
    log('[PortlandModal] ✅ Siding color options updated');
  }

  /**
   * Find section by ID across all steps
   */
  findSectionById(sectionId) {
    if (!this.steps) return null;
    for (const step of this.steps) {
      if (step.sections) {
        const section = step.sections.find(s => s.id === sectionId);
        if (section) return section;
      }
    }
    return null;
  }

  /**
   * Update dependent sections based on showWhen conditions
   * Re-renders the current step to show/hide conditional sections
   */
  updateDependentSections() {
    const currentStepData = this.steps.find(s => s.step === this.currentStep);
    if (!currentStepData || !currentStepData.sections) return;
    
    log('Checking dependent sections for step:', this.currentStep);
    
    // Check if any sections have showWhen conditions
    const hasConditionalSections = currentStepData.sections.some(s => s.showWhen || s.hidden);
    
    if (hasConditionalSections) {
      log('Found conditional sections, re-rendering step');
      this.populateStepOptions(this.currentStep);
    }
  }

  /**
   * Update 3D viewer for panel-based models
   */
  /**
   * Update 3D viewer for panel-based models
   * CRITICAL: Ensures texture and color are passed correctly
   */
  update3DViewerPanelBased(sectionId, option) {
    if (!this.viewer3D) {
      console.error('❌ 3D viewer not available');
      return;
    }

    console.log('\n🔄 Updating 3D viewer:');
    console.log('  Section:', sectionId);
    console.log('  Value:', option.value);
    console.log('  Has texture:', !!option.texture);
    console.log('  Has color:', !!option.color?.hex);
    console.log('  Has modelFile:', !!option.modelFile);

    this.viewer3D.updateConfiguration({
      section: sectionId,
      value: option.value,
      modelFile: option.modelFile,
      hex: option.color?.hex || option.hex,
      texture: option.texture
    });
    
    console.log('✅ 3D viewer update dispatched\n');
  }

  renderGroupedSections(options, container) {
    const processedSections = new Set();

    Object.entries(PortlandConfigurator.SECTION_GROUPS).forEach(([groupName, groupKeys]) => {
      const groupSections = groupKeys
        .filter(key => options[key] && !this.isHidden(options[key]) && this.hasValidOptions(options[key]))
        .map(key => ({ key, data: options[key] }));

      if (groupSections.length > 0) {
        const groupContainer = document.createElement('div');
        groupContainer.className = 'config-group';

        const groupHeader = document.createElement('h3');
        groupHeader.className = 'config-group-header';
        groupHeader.textContent = `${groupName}:`;
        groupContainer.appendChild(groupHeader);

        groupSections.forEach(({ key, data }) => {
          groupContainer.appendChild(this.createConfigSection(key, data, true));
          processedSections.add(key);
        });

        container.appendChild(groupContainer);
      }
    });

    Object.keys(options).forEach(key => {
      if (processedSections.has(key)) return;
      const data = options[key];
      if (!data || this.isHidden(data) || !this.hasValidOptions(data)) return;
      container.appendChild(this.createConfigSection(key, data));
    });
  }

  renderFlatSections(options, container) {
    Object.keys(options).forEach(key => {
      const data = options[key];
      if (!data || this.isHidden(data) || !this.hasValidOptions(data)) return;
      container.appendChild(this.createConfigSection(key, data));
    });
  }

  isHidden(data) {
    return data.hide === true || data.hide === 'true';
  }

  hasValidOptions(data) {
    return (Array.isArray(data.options) && data.options.length > 0) || this.sectionHasNestedOptions(data);
  }

  sectionHasNestedOptions(data) {
    if (!data || typeof data !== 'object') return false;
    return Object.keys(data).some(key => {
      if (['meshGroups', 'displayName', 'textureScale'].includes(key)) return false;
      const sub = data[key];
      return sub && Array.isArray(sub.options) && sub.options.length > 0;
    });
  }

  createConfigSection(sectionKey, sectionData, isGrouped = false) {
    if (this.isHidden(sectionData)) return document.createDocumentFragment();

    const section = document.createElement('div');
    section.className = 'config-section';
    section.dataset.configSection = sectionKey;

    const hasNestedSections = !sectionData.options && this.sectionHasNestedOptions(sectionData);

    if (hasNestedSections) {
      this.renderNestedSection(section, sectionKey, sectionData);
    } else if (sectionData.options) {
      this.renderFlatSection(section, sectionKey, sectionData, isGrouped);
    }

    return section;
  }

  renderNestedSection(section, sectionKey, sectionData) {
    const displayName = sectionData.displayName || formatSectionName(sectionKey);
    const header = document.createElement('h3');
    header.className = 'config-group-header';
    header.textContent = `${displayName}:`;
    section.appendChild(header);

    Object.keys(sectionData).forEach(subKey => {
      if (['meshGroups', 'pricePerSqFt', 'hide', 'displayName', 'textureScale'].includes(subKey)) return;

      const subSection = sectionData[subKey];
      if (!subSection?.options?.length || this.isHidden(subSection)) return;

      const subSectionEl = document.createElement('div');
      subSectionEl.className = 'config-subsection';

      const subDisplayName = PortlandConfigurator.DISPLAY_NAME_OVERRIDES[subKey] || subSection.displayName || formatSectionName(subKey);

      const isPackageSection = subSection.options?.some(opt => {
        const name = String(opt.displayName || opt.value || '').toLowerCase().replace(/[\s_-]/g, '');
        return name.includes('shellonly') || name.includes('lifestyleinterior') || name.includes('fullyequip');
      });
      if (isPackageSection) {
        subSection.options.sort((a, b) => {
          const getScore = (opt) => {
            const name = String(opt.displayName || opt.value || '').toLowerCase().replace(/[\s_-]/g, '');
            if (name.includes('fullyequip')) return 1;
            if (name.includes('lifestyleinterior')) return 1;
            if (name.includes('shellonly')) return 2;
            return 3;
          };
          return getScore(a) - getScore(b);
        });
        // Force default assignment to the top-ranked package if not manually selected by user
        if (typeof this.hasForcedPackageDefaultSub === 'undefined') {
          if (!this.currentConfig[sectionKey]) this.currentConfig[sectionKey] = {};
          this.currentConfig[sectionKey][subKey] = subSection.options[0].value;
          this.hasForcedPackageDefaultSub = true;
        }
      }
      
      let defaultValue = this.currentConfig[sectionKey]?.[subKey];
      const selectedOption = subSection.options.find(opt => opt.value === defaultValue) || subSection.options[0];
      
      if (!defaultValue && selectedOption) {
        if (!this.currentConfig[sectionKey]) this.currentConfig[sectionKey] = {};
        this.currentConfig[sectionKey][subKey] = selectedOption.value;
        defaultValue = selectedOption.value;
      }
      
      const selectedDisplayName = selectedOption?.displayName || formatOptionValue(selectedOption?.value) || '';

      const title = document.createElement('h4');
      title.className = 'config-subtitle';
      title.dataset.configTitle = `${sectionKey}.${subKey}`;
      title.innerHTML = `<span class="config-title-label">${subDisplayName}:</span> <span class="config-title-value">${selectedDisplayName}</span>`;
      subSectionEl.appendChild(title);

      const optionsContainer = this.createOptionsContainer(`${sectionKey}.${subKey}`, sectionKey, subKey);
      this.populateOptions(optionsContainer, `${sectionKey}.${subKey}`, subSection.options, defaultValue);
      
      if (optionsContainer.children.length > 0) {
        subSectionEl.appendChild(optionsContainer);
        section.appendChild(subSectionEl);
      }
    });
  }

  renderFlatSection(section, sectionKey, sectionData, isGrouped) {
    const displayName = PortlandConfigurator.DISPLAY_NAME_OVERRIDES[sectionKey] || sectionData.displayName || formatSectionName(sectionKey);

    const isPackageSection = sectionData.options?.some(opt => {
      const name = String(opt.displayName || opt.value || '').toLowerCase().replace(/[\s_-]/g, '');
      return name.includes('shellonly') || name.includes('lifestyleinterior') || name.includes('fullyequip');
    });
    if (isPackageSection) {
      sectionData.options.sort((a, b) => {
        const getScore = (opt) => {
          const name = String(opt.displayName || opt.value || '').toLowerCase().replace(/[\s_-]/g, '');
          if (name.includes('fullyequip')) return 1;
          if (name.includes('lifestyleinterior')) return 1;
          if (name.includes('shellonly')) return 2;
          return 3;
        };
        return getScore(a) - getScore(b);
      });
      // Force default assignment to the top-ranked package if not manually selected by user
      if (typeof this.hasForcedPackageDefaultFlat === 'undefined') {
        this.currentConfig[sectionKey] = sectionData.options[0].value;
        this.hasForcedPackageDefaultFlat = true;
      }
    }

    const optionsContainer = this.createOptionsContainer(sectionKey, sectionKey);
    
    let defaultValue = this.currentConfig[sectionKey];
    this.populateOptions(optionsContainer, sectionKey, sectionData.options, defaultValue);

    if (optionsContainer.children.length === 0) return;

    const selectedOption = sectionData.options?.find(opt => opt.value === defaultValue) || sectionData.options[0];
    
    if (!defaultValue && selectedOption) {
      this.currentConfig[sectionKey] = selectedOption.value;
      defaultValue = selectedOption.value;
    }
    
    const selectedDisplayName = selectedOption?.displayName || formatOptionValue(selectedOption?.value) || '';

    const title = document.createElement(isGrouped ? 'h4' : 'h3');
    title.className = isGrouped ? 'config-subtitle' : 'config-title';
    title.dataset.configTitle = sectionKey;
    title.innerHTML = `<span class="config-title-label">${displayName}:</span> <span class="config-title-value">${selectedDisplayName}</span>`;
    
    section.appendChild(title);
    section.appendChild(optionsContainer);
  }

  createOptionsContainer(key, sectionKey, subKey) {
    const container = document.createElement('div');
    const checkKey = (subKey || sectionKey).toLowerCase();
    
    let className = 'config-options';
    if (checkKey.includes('color') || ['main', 'accent'].includes(checkKey)) {
      className += ' config-options--colors';
    } else if (/floor|counter|cabinet|fixture|flooring/.test(checkKey)) {
      className += ' config-options--swatches';
    }

    container.className = className;
    container.dataset.configOptions = key;
    return container;
  }

  populateOptions(container, sectionKey, options, defaultValue) {
    let firstVisibleIndex = 0;
    options.forEach(option => {
      if (this.isHidden(option)) return;
      const isSelected = defaultValue ? option.value === defaultValue : firstVisibleIndex === 0;
      firstVisibleIndex++;
      container.appendChild(this.createOptionCard(sectionKey, option, isSelected));
    });
  }

  createOptionCard(sectionKey, option, isSelected) {
    const card = document.createElement('div');
    const isColor = option.hex || option.rgb;
    const isTexture = option.thumbnail || option.texture;

    let classes = 'option-card';
    if (isSelected) classes += ' selected';
    if (isColor) classes += ' option-card--color';
    if (isTexture) classes += ' option-card--texture';

    card.className = classes;
    card.setAttribute('data-option-card', '');
    card.dataset.section = sectionKey;
    card.dataset.value = option.value;
    card.dataset.price = option.price || 0;

    if (option.meshGroups) card.dataset.meshGroups = JSON.stringify(option.meshGroups);
    if (option.hex) card.dataset.hex = option.hex;
    if (option.rgb) card.dataset.rgb = option.rgb;
    if (option.texture) card.dataset.texture = option.texture;
    if (option.textureScale) card.dataset.textureScale = JSON.stringify(option.textureScale);

    let html = '<span class="option-card-check">✓</span>';

    if (isColor) {
      html += `<div class="option-card-swatch" style="background-color: ${option.hex || option.rgb};"></div>`;
    } else if (isTexture) {
      html += `<div class="option-card-thumbnail"><img src="${option.texture || option.thumbnail}" alt="${option.displayName}" loading="lazy"></div>`;
    } else {
      const icon = option.iconImage || option.image
        ? `<img src="${option.iconImage || option.image}" alt="${option.displayName}" width="48" height="48" loading="lazy">`
        : '<svg viewBox="0 0 48 48" stroke-width="1.5" stroke="currentColor" fill="none"><rect x="8" y="8" width="32" height="32" rx="4"/><circle cx="24" cy="24" r="8"/></svg>';
      html += `<div class="option-card-icon">${icon}</div>`;
    }

    html += `<span class="option-card-label">${option.displayName}</span>`;
    html += option.price > 0
      ? `<span class="option-card-price">+${formatPrice(option.price)}</span>`
      : '<span class="option-card-included">Included</span>';

    card.innerHTML = html;
    card.addEventListener('click', () => this.selectOption(sectionKey, card, option));
    return card;
  }

  // ============================================================================
  // OPTION SELECTION
  // ============================================================================

  selectOption(section, card, option) {
    log('selectOption called:', section, option.value);
    
    // DEBUG: Log siding selection
    if (section === 'siding') {
      console.log('[PortlandModal] ===== SIDING SELECTED =====');
      console.log('[PortlandModal] option.value:', option.value);
      console.log('[PortlandModal] option.displayName:', option.displayName);
      console.log('[PortlandModal] this.currentConfig.siding:', this.currentConfig.siding);
      console.log('[PortlandModal] =========================');
    }
    
    const container = card.parentElement;
    qa('[data-option-card]', '.option-card', container).forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    const titleEl = $(`[data-config-title="${section}"]`, this.step2Section);
    if (titleEl) {
      const valueEl = titleEl.querySelector('.config-title-value, [data-config-title-value]');
      if (valueEl) valueEl.textContent = option.displayName || option.value;
    }

    if (section.includes('.')) {
      const [parentKey, childKey] = section.split('.');
      if (!this.currentConfig[parentKey]) this.currentConfig[parentKey] = {};
      this.currentConfig[parentKey][childKey] = option.value;
    } else {
      this.currentConfig[section] = option.value;
    }

    // STATE RESET: If form was already submitted and user changes options,
    // invalidate the form data (requires re-submission before accessing Summary).
    // ⚠️ DO NOT call the installation API here - only clear form state.
    console.log('[Portland] selectOption - checking state reset', {
      section,
      hasValidUserFormData: this.hasValidUserFormData(),
      userFormData: this.userFormData,
      finalPriceData: !!this.finalPriceData,
      _finalPriceDataZip: this._finalPriceDataZip
    });
    if (this.hasValidUserFormData()) {
      console.log('[Portland] Clearing form data due to option change');
      this.userFormData = null;
      window.studioShedUserData = null;
      this.finalPriceData = null;
      this._finalPriceDataZip = null; // Clear cached ZIP so API is called again on next form submit
      // Disable Summary tab immediately
      this.updateSummaryTabState();
    }

    // Update price display (skipQueueUpdate=true to prevent API call)
    this.updateTotalPrice(true);
    
    // After re-rendering, ensure sidingColor section visibility is correct for cedar
    // This is needed for orientation-based models that may re-render
    if (section === 'siding') {
      console.log('[PortlandModal] ===== POST-RENDER CHECK (selectOption) =====');
      console.log('[PortlandModal] Checking sidingColor section visibility after re-render');
      console.log('[PortlandModal] option.value:', option.value);
      console.log('[PortlandModal] this.currentConfig.siding:', this.currentConfig.siding);
      
      const colorSectionEl = $('[data-config-section="sidingColor"]', this.step2Section);
      console.log('[PortlandModal] colorSectionEl found:', !!colorSectionEl);
      
      if (colorSectionEl) {
        const sidingValue = String(option.value || this.currentConfig.siding || '').toLowerCase().trim();
        console.log('[PortlandModal] sidingValue (normalized):', sidingValue);
        
        // More comprehensive cedar detection - check for cedar in any form
        const isCedarSiding = sidingValue.includes('cedar') || 
                             sidingValue === 'horiz_plank' || 
                             sidingValue === 'cedar-plank' || 
                             sidingValue === 'cedar-shake' ||
                             sidingValue === 'cedar_shake' ||
                             sidingValue === 'cedarplank' ||
                             sidingValue === 'cedarshake' ||
                             sidingValue.replace(/[\s_-]/g, '').includes('cedar');
        
        console.log('[PortlandModal] isCedarSiding:', isCedarSiding);
        
        if (isCedarSiding) {
          colorSectionEl.style.display = 'none';
          delete this.currentConfig.sidingColor;
          console.log('[PortlandModal] ✓ Post-render: Hid sidingColor section for cedar siding:', sidingValue);
        } else {
          colorSectionEl.style.display = '';
          console.log('[PortlandModal] ✓ Post-render: Showing sidingColor section (not cedar):', sidingValue);
        }
      } else {
        console.log('[PortlandModal] ⚠️ colorSectionEl not found in DOM after re-render');
      }
      console.log('[PortlandModal] ============================');
    }
    
    // Check if this is a panel-based model
    const isPanelBased = this.modelConfig?.loadingSequence?.length > 0;
    if (isPanelBased && option.modelFile) {
      this.update3DViewerPanelBased(section, option);
    } else {
      this.update3DViewer(section, option);
    }

    document.dispatchEvent(new CustomEvent('config:changed', {
      detail: { 
        section, 
        value: option.value, 
        meshGroups: extractMeshGroups(option.meshGroups), 
        texture: this.resolveTextureReference(option.texture), 
        modelFile: option.modelFile,
        config: this.currentConfig 
      }
    }));
  }

  update3DViewer(section, option) {
    if (!this.viewer3D) return;

    const currentStepData = this.steps.find(s => s.step === this.currentStep);
    let sectionData = null, allOptions = [], sectionMeshGroups = [];

    if (section.includes('.')) {
      const [parentKey, childKey] = section.split('.');
      const parentSection = currentStepData?.options?.[parentKey];
      sectionMeshGroups = parentSection?.meshGroups || [];
      sectionData = parentSection?.[childKey];
      allOptions = sectionData?.options || [];
    } else {
      sectionData = currentStepData?.options?.[section];
      allOptions = sectionData?.options || [];
      sectionMeshGroups = sectionData?.meshGroups || [];
    }

    const optionMeshGroups = extractMeshGroups(option.meshGroups);
    const sectionMeshGroupsArray = extractMeshGroups(sectionMeshGroups);
    const meshGroupsToUse = optionMeshGroups.length > 0 ? optionMeshGroups : sectionMeshGroupsArray;

    this.viewer3D.updateConfiguration({
      section,
      value: option.value,
      orientation: section === 'orientation' ? option.value : this.currentConfig.orientation,
      meshGroups: meshGroupsToUse,
      allOptions,
      hex: option.hex,
      rgb: option.rgb,
      texture: this.resolveTextureReference(option.texture),
      textureScale: option.textureScale || sectionData?.textureScale
    });
  }

  // ============================================================================
  // 3D VIEWER
  // ============================================================================

  init3DViewer() {
    const viewerContainer = $('[data-model-viewer]', this.step2Section);
    if (!viewerContainer) {
      console.error('[PortlandModal] ❌ Model viewer container not found');
      return;
    }

    const placeholder = viewerContainer.querySelector('[data-model-placeholder], .model-placeholder');
    if (placeholder) {
      // Use visibility/opacity to maintain layout stability on mobile (Portland-specific fix)
      placeholder.style.visibility = 'visible';
      placeholder.style.opacity = '1';
      placeholder.style.pointerEvents = 'auto';
      // Position placeholder absolutely to overlay without affecting layout
      placeholder.style.position = 'absolute';
      placeholder.style.top = '0';
      placeholder.style.left = '0';
      placeholder.style.right = '0';
      placeholder.style.bottom = '0';
      placeholder.style.zIndex = '10';
      // Hide the placeholder image during loading - only show spinner
      const placeholderImg = placeholder.querySelector('[data-placeholder-image], .placeholder-image');
      if (placeholderImg) placeholderImg.style.display = 'none';
      const loading = placeholder.querySelector('[data-loading-indicator], .loading-indicator');
      if (loading) loading.style.display = 'flex';
    }
    
    // Ensure the viewer container has relative/sticky positioning for absolute placeholder
    const currentPosition = getComputedStyle(viewerContainer).position;
    if (currentPosition !== 'sticky') {
      viewerContainer.style.position = 'relative';
    }

    if (this.viewer3D) {
      this.viewer3D.dispose();
      this.viewer3D = null;
    }

    const configData = this.buildConfigForViewer();
    if (!configData) {
      console.error('[PortlandModal] ❌ Could not build config for viewer');
      this.showPlaceholderOnError(placeholder);
      return;
    }

    log('🎬 Config data built for viewer:', {
      hasLoadingSequence: !!configData.loadingSequence,
      sequenceLength: configData.loadingSequence?.length || 0,
      hasModels: !!configData.models,
      hasMaterialGroups: !!configData.materialGroups,
      materialGroupKeys: Object.keys(configData.materialGroups || {})
    });

    // Helper to hide the 3D canvas during loading (Portland-specific fix)
    const hideCanvas = () => {
      const canvas = viewerContainer.querySelector('canvas');
      if (canvas) {
        canvas.style.opacity = '0';
        canvas.style.visibility = 'hidden';
      }
    };

    // Helper to show the 3D canvas after loading completes (Portland-specific fix)
    const showCanvas = () => {
      const canvas = viewerContainer.querySelector('canvas');
      if (canvas) {
        canvas.style.opacity = '1';
        canvas.style.visibility = 'visible';
        canvas.style.transition = 'opacity 0.3s ease-in-out';
      }
    };

    // Helper to show loading state without layout shift (Portland-specific mobile fix)
    const showLoadingState = () => {
      if (placeholder) {
        placeholder.style.visibility = 'visible';
        placeholder.style.opacity = '1';
        placeholder.style.pointerEvents = 'auto';
        const loading = placeholder.querySelector('[data-loading-indicator], .loading-indicator');
        if (loading) loading.style.display = 'flex';
        const img = placeholder.querySelector('[data-placeholder-image], .placeholder-image');
        if (img) img.style.display = 'none';
      }
    };

    // Helper to hide loading state without layout shift (Portland-specific mobile fix)
    const hideLoadingState = () => {
      if (placeholder) {
        placeholder.style.visibility = 'hidden';
        placeholder.style.opacity = '0';
        placeholder.style.pointerEvents = 'none';
        placeholder.style.transition = 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out';
        const loading = placeholder.querySelector('[data-loading-indicator], .loading-indicator');
        if (loading) loading.style.display = 'none';
        const img = placeholder.querySelector('[data-placeholder-image], .placeholder-image');
        if (img) img.style.display = 'none';
      }
    };

    // Use the global promise if available, otherwise fallback to polling
    const modulePromise = window.portlandModuleReady || this.waitFor3DModule();
    
    modulePromise.then(() => {
      const Configurator3D = window.PortlandConfigurator3D;
      if (!Configurator3D) {
        console.error('[PortlandModal] ❌ PortlandConfigurator3D not found on window even after promise resolved');
        console.error('This should never happen. Check portland-configurator-3d.js exports.');
        this.showPlaceholderOnError(placeholder);
        return;
      }
      
      log('✅ PortlandConfigurator3D found, initializing...');
      log('   Constructor type:', typeof Configurator3D);
      log('   Current step before 3D init:', this.currentStep);
      
      this.viewer3D = new Configurator3D(viewerContainer, configData);
      
      // CRITICAL: Set currentStep on viewer BEFORE init
      // This ensures correct initial view (step 1 = interior/top-down, step 2+ = exterior/front)
      this.viewer3D.currentStep = this.currentStep || 1;
      log('   Set viewer.currentStep to:', this.viewer3D.currentStep);
      
      this.viewer3D.init();

      // Initially hide the canvas until model is loaded
      hideCanvas();

      viewerContainer.addEventListener('configurator:modelLoading', () => {
        log('🔄 Model loading...');
        // Hide canvas during loading to prevent visible model shift
        hideCanvas();
        // Show loading state without causing layout shift on mobile
        showLoadingState();
      });

      viewerContainer.addEventListener('configurator:modelLoaded', () => {
        log('✅ Model loaded successfully');
        
        // Apply any additional config BEFORE showing canvas
        this.applyConfigTo3D(this.currentConfig);
        
        // ENSURE correct view is set (roof hidden for step 1, visible for others)
        log(`Ensuring correct view for current step: ${this.currentStep}`);
        const currentStepData = this.steps.find(s => s.step === this.currentStep);
        if (currentStepData) {
          const stepName = currentStepData.name || currentStepData.id;
          log(`Step name: ${stepName}`);
          this.update3DViewForStep(stepName);
        } else {
          log('⚠️ Could not find current step data, defaulting to step 1');
          if (this.viewer3D) {
            this.viewer3D.setViewForStep(1);
          }
        }
        
        // Small delay to ensure model is fully positioned before revealing
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Hide loading state without layout shift, then show canvas
            hideLoadingState();
            showCanvas();
          });
        });
      });

      // Detect loading mode and call appropriate method
      const isPanelBased = configData.loadingSequence?.length > 0;
      
      if (isPanelBased) {
        log('🚀 Starting PANEL-BASED loading...');
        this.viewer3D.loadPanels(this.currentConfig).catch((error) => {
          console.error('❌ Error loading panels:', error);
          this.showPlaceholderOnError(placeholder);
        });
      } else {
        log('🚀 Starting ORIENTATION-BASED loading...');
        const defaultOrientation = this.currentConfig.orientation || this.defaultConfig.orientation || 'left';
        this.viewer3D.loadModel(defaultOrientation).catch((error) => {
          console.error('❌ Error loading model:', error);
          this.showPlaceholderOnError(placeholder);
        });
      }
    }).catch((error) => {
      console.error('❌ Error waiting for 3D module:', error);
      this.showPlaceholderOnError(placeholder);
    });
  }

  buildConfigForViewer() {
    if (!this.modelConfig) return null;

    // Detect if this is a panel-based model
    const isPanelBased = this.modelConfig?.loadingSequence?.length > 0;
    
    log('Building config for viewer:', {
      isPanelBased,
      hasLoadingSequence: !!this.modelConfig?.loadingSequence,
      hasModelFiles: !!this.modelsData?.modelFiles,
      hasMaterialGroups: !!this.modelConfig?.materialGroups
    });

    // PANEL-BASED MODEL CONFIG (NEW)
    if (isPanelBased) {
      return {
        model: this.modelConfig.model || this.modelsData || {},
        steps: this.steps,
        defaultConfiguration: this.defaultConfig || {},
        loadingSequence: this.modelConfig.loadingSequence || [],
        materialGroups: this.modelConfig.materialGroups || {},
        sceneSettings: this.modelConfig.sceneSettings || {},
        textures: this.texturesData || {},
        colors: this.colorsData || {},
        disableMtl: true // Portland models don't have MTL files - prevents CORS errors
      };
    }

    // ORIENTATION-BASED MODEL CONFIG (LEGACY)
    const modelName = this.modelsData?.name || this.modelConfig?.name || 'portland-1';
    const modelKey = (this.modelConfig?.model || modelName).toLowerCase().replace(/\s+/g, '-');

    const models = {
      [modelKey]: {
        name: modelName,
        squareFootage: this.modelsData?.squareFootage || this.modelConfig?.squareFootage,
        bedBath: this.modelsData?.bedBath || this.modelConfig?.bedBath,
        description: this.modelsData?.description || this.modelConfig?.description,
        basePrice: this.modelsData?.basePrice || this.modelConfig?.basePrice,
        modelFiles: this.modelsData?.modelFiles || this.modelConfig?.modelFiles || {}
      }
    };

    const configurationOptions = {};
    const interiorFinishes = {};
    const interiorFinishKeys = ['mainFloorFinish', 'flooring', 'bathFloorFinish', 'counterFinish', 'countertop', 'cabinetFinish', 'cabinets', 'fixtureFinish', 'fixture'];

    this.steps.forEach(step => {
      if (!step.options) return;
      Object.keys(step.options).forEach(optionKey => {
        const optionData = step.options[optionKey];
        const sectionMeshGroups = Array.isArray(optionData.meshGroups) ? optionData.meshGroups : [];

        configurationOptions[optionKey] = {
          displayName: optionData.displayName || formatSectionName(optionKey),
          options: optionData.options || [],
          meshGroups: sectionMeshGroups
        };

        if (interiorFinishKeys.includes(optionKey)) {
          interiorFinishes[optionKey] = configurationOptions[optionKey];
        }

        const nestedKeys = ['main', 'accent', 'bath', 'fixture', 'countertop', 'cabinets'];
        nestedKeys.forEach(subKey => {
          if (optionData[subKey]?.options) {
            const subData = optionData[subKey];
            const subMeshGroups = Array.isArray(subData.meshGroups) ? subData.meshGroups : [];
            configurationOptions[`${optionKey}.${subKey}`] = {
              displayName: subData.displayName || formatSectionName(subKey),
              options: subData.options || [],
              meshGroups: subMeshGroups,
              textureScale: subData.textureScale
            };
          }
        });
      });
    });

    return {
      models,
      textures: this.texturesData || {},
      colors: this.colorsData || {},
      configurationOptions,
      defaultConfiguration: this.defaultConfig || {},
      meshGroups: this.meshGroupsData || {},
      interiorFinishes: this.modelConfig.interiorFinishes || interiorFinishes,
      steps: this.steps
    };
  }

  applyConfigTo3D(config) {
    if (!this.viewer3D || !config) return;

    const applyValue = (sectionKey, value, optionData, fallbackMeshGroups = []) => {
      if (!optionData?.options?.length) return;
      const selectedOption = optionData.options.find(o => o.value === value);
      if (!selectedOption) return;

      const optionMeshGroups = extractMeshGroups(selectedOption.meshGroups);
      const sectionMeshGroups = extractMeshGroups(optionData.meshGroups);
      const meshGroupsToUse = optionMeshGroups.length > 0 ? optionMeshGroups : (sectionMeshGroups.length > 0 ? sectionMeshGroups : extractMeshGroups(fallbackMeshGroups));

      this.viewer3D.updateConfiguration({
        section: sectionKey,
        value,
        meshGroups: meshGroupsToUse,
        hex: selectedOption.hex,
        rgb: selectedOption.rgb,
        texture: this.resolveTextureReference(selectedOption.texture),
        textureScale: selectedOption.textureScale || optionData.textureScale,
        allOptions: optionData.options,
        skipFocus: true
      });
    };

    Object.keys(config).forEach(key => {
      const value = config[key];
      if (value === undefined || value === null) return;

      if (typeof value === 'object' && !Array.isArray(value)) {
        Object.keys(value).forEach(subKey => {
          const subValue = value[subKey];
          if (subValue === undefined || subValue === null) return;
          let subOptionData = null, parentMeshGroups = [];
          this.steps.some(step => {
            if (step.options?.[key]?.[subKey]) {
              subOptionData = step.options[key][subKey];
              parentMeshGroups = extractMeshGroups(step.options[key].meshGroups);
              return true;
            }
          });
          if (subOptionData) applyValue(`${key}.${subKey}`, subValue, subOptionData, parentMeshGroups);
        });
      } else {
        let optionData = null;
        this.steps.some(step => {
          if (step.options?.[key]) {
            optionData = step.options[key];
            return true;
          }
        });
        if (optionData) applyValue(key, value, optionData);
      }
    });
  }

  setViewForCurrentStep() {
    if (!this.viewer3D) return;
    const currentStepData = this.steps.find(s => s.step === this.currentStep);
    const stepName = currentStepData?.name?.toLowerCase() || '';
    const isTopDown = ['floorplan', 'layout', 'interior'].includes(stepName);
    this.viewer3D.setViewForStep(isTopDown ? 1 : 2);
  }

  showPlaceholderOnError(placeholder) {
    if (!placeholder) return;
    // Show placeholder image only on error as fallback (Portland-specific fix)
    placeholder.style.visibility = 'visible';
    placeholder.style.opacity = '1';
    placeholder.style.pointerEvents = 'auto';
    const img = placeholder.querySelector('[data-placeholder-image], .placeholder-image');
    if (img) img.style.display = 'block';
    const loading = placeholder.querySelector('[data-loading-indicator], .loading-indicator');
    if (loading) loading.style.display = 'none';
  }

  waitFor3DModule(timeout = 30000) {
    return new Promise((resolve, reject) => {
      log('Waiting for PortlandConfigurator3D module...');
      
      if (window.PortlandConfigurator3D) {
        log('✅ PortlandConfigurator3D already available');
        resolve();
        return;
      }
      
      // Listen for module ready event (faster than polling)
      const moduleReadyHandler = () => {
        log('✅ Received portland-3d-module-ready event');
        clearInterval(checkInterval);
        window.removeEventListener('portland-3d-module-ready', moduleReadyHandler);
        resolve();
      };
      window.addEventListener('portland-3d-module-ready', moduleReadyHandler, { once: true });
      
      const startTime = Date.now();
      let attempts = 0;
      
      const checkInterval = setInterval(() => {
        attempts++;
        const elapsed = Date.now() - startTime;
        
        if (window.PortlandConfigurator3D) {
          clearInterval(checkInterval);
          window.removeEventListener('portland-3d-module-ready', moduleReadyHandler);
          log(`✅ PortlandConfigurator3D loaded after ${elapsed}ms (${attempts} attempts)`);
          resolve();
        } else if (elapsed > timeout) {
          clearInterval(checkInterval);
          window.removeEventListener('portland-3d-module-ready', moduleReadyHandler);
          
        
          
          reject(new Error('Timeout waiting for 3D module. See detailed error above.'));
        } else if (attempts % 50 === 0) {
          log(`⏳ Still waiting for module... ${elapsed}ms elapsed`);
        }
      }, 100);
    });
  }

  // ============================================================================
  // HEADER & PRICING
  // ============================================================================

  updateStep2Header(variantInfo = {}) {
    const titleEl = $('[data-configurator-title]', this.step2Section);
    const specsEl = $('[data-configurator-specs]', this.step2Section);
    // Get ALL price elements (header and mobile footer)
    const priceEls = $$('[data-configurator-price]', this.step2Section);

    // Title: Product title + variant title (e.g., "Portland Shell Only")
    const productTitle = variantInfo.productTitle || '';
    const modelSize = this.modelsData?.size || this.modelConfig?.model?.size || this.modelConfig?.size || '';
    const sqFt = this.modelsData?.squareFootage || this.modelConfig?.model?.dimensions?.squareFeet || variantInfo.sqFt;
    const variantTitle = variantInfo.title || '';

    if (titleEl) {
      titleEl.textContent = productTitle + (variantTitle ? ` ${variantTitle}` : '');
    }
    
    // Specs: sq ft + size (e.g., "128 sq ft | 8x16")
    if (specsEl) {
      const specs = [modelSize , sqFt && `${sqFt} sq ft` ].filter(Boolean);
      specsEl.textContent = specs.join(' | ');
    }

    this.basePrice = this.modelsData?.basePrice || this.modelConfig?.model?.basePrice || variantInfo.price || 0;
    // Update ALL price elements
    priceEls.forEach(el => el.textContent = formatPrice(this.basePrice));
    
    log('Updated header:', { productTitle, modelSize, sqFt, variantTitle, basePrice: this.basePrice });
  }

  updateNextButtonText(currentStepNumber) {
    const nextBtnText = $('[data-next-step-text]', this.step2Section);
    if (nextBtnText) {
      const nextStep = this.steps.find(s => s.step === currentStepNumber + 1);
      nextBtnText.textContent = nextStep ? nextStep.name : 'Services';
    }
    
    const backBtn = $('[data-back-step]', this.step2Section);
    if (backBtn) {
      backBtn.style.display = currentStepNumber === 1 ? 'none' : '';
    }
  }

  updateTotalPrice() {
    let additionalPrice = 0;

    // Calculate price based on model type
    const isPanelBased = this.modelConfig?.loadingSequence?.length > 0;
    
    if (isPanelBased) {
      // Panel-based pricing (sections format)
      this.steps.forEach(step => {
        if (!step.sections) return;
        step.sections.forEach(section => {
          const configValue = this.currentConfig[section.id];
          if (!configValue) return;
          const selectedOption = section.options?.find(opt => opt.value === configValue);
          if (selectedOption?.price) {
            additionalPrice += selectedOption.price;
            log(`Adding ${section.id} price: ${selectedOption.price}`);
          }
        });
      });
    } else {
      // Orientation-based pricing (options format)
      this.steps.forEach(step => {
        if (!step.options) return;
        Object.keys(step.options).forEach(sectionKey => {
          const sectionData = step.options[sectionKey];
          const nestedKeys = ['main', 'bath', 'fixture', 'accent', 'countertop', 'cabinets'];
          
          if (nestedKeys.some(k => sectionData[k])) {
            nestedKeys.forEach(subKey => {
              if (!sectionData[subKey]?.options) return;
              const configValue = this.currentConfig[sectionKey]?.[subKey];
              const selectedOption = sectionData[subKey].options.find(opt => opt.value === configValue);
              if (selectedOption?.price) additionalPrice += selectedOption.price;
            });
          } else if (sectionData.options) {
            const configValue = this.currentConfig[sectionKey];
            const selectedOption = sectionData.options.find(opt => opt.value === configValue);
            if (selectedOption?.price) additionalPrice += selectedOption.price;
          }
        });
      });
    }

    // Base product option price (excludes shipping/installation/permit)
    this.configPrice = additionalPrice;

    // Add installation + shipping costs from API (only after ZIP is known)
    let servicesPrice = 0;
    if (this.hasValidUserFormData()) {
      const apiShipping = normalizeNumber(this.finalPriceData?.shipping_cost, 0);
      const apiInstall = window.ConfiguratorShared.computeInstallationCost(
        this.finalPriceData?.breakdown?.installation,
        this.currentConfig?.installation
      );
      servicesPrice = apiShipping + apiInstall;
    }

    const totalPrice = this.basePrice + additionalPrice + servicesPrice + (this.permitPlanPrice || 0);

    log('Price calculation:', { base: this.basePrice, additional: additionalPrice, total: totalPrice });

    // Update ALL price elements (header and mobile footer)
    const priceEls = $$('[data-configurator-price]', this.step2Section);
    priceEls.forEach(el => el.textContent = formatPrice(totalPrice));

    // Update ALL monthly price elements
    const monthlyEls = $$('[data-monthly-price]', this.step2Section);
    monthlyEls.forEach(el => el.textContent = formatPrice(Math.round(totalPrice / 180)));

    // Only queue update if explicitly requested and not already in progress
    // This prevents infinite loops - queueFinalPriceUpdate will call updateTotalPrice with skipQueueUpdate=true
    // Also skip if we have valid price data for the current ZIP code
    const currentZip = this.userFormData?.zipCode;
    const hasValidPriceData = this.finalPriceData && this._finalPriceDataZip && 
                              this._finalPriceDataZip === currentZip;
    if (!skipQueueUpdate && this.hasValidUserFormData() && !this._priceUpdateQueued && !hasValidPriceData) {
      this._priceUpdateQueued = true;
      // Use requestAnimationFrame to defer the API call and prevent blocking
      requestAnimationFrame(() => {
        this._priceUpdateQueued = false;
        this.queueFinalPriceUpdate();
      });
    }
  }

  updateTotalPrice(skipQueueUpdate = false) {
    // Prevent infinite loops by allowing callers to skip the queue update
    if (this._updatingPrice) return; // Guard against recursive calls
    this._updatingPrice = true;

    try {
      let additionalPrice = 0;

      // Calculate price based on model type
      const isPanelBased = this.modelConfig?.loadingSequence?.length > 0;
      
      if (isPanelBased) {
        // Panel-based pricing (sections format)
        this.steps.forEach(step => {
          if (!step.sections) return;
          step.sections.forEach(section => {
            if (section.id === 'installation') return;
            const configValue = this.currentConfig[section.id];
            if (!configValue) return;
            const selectedOption = section.options?.find(opt => opt.value === configValue);
            if (selectedOption?.price) {
              additionalPrice += selectedOption.price;
              log(`Adding ${section.id} price: ${selectedOption.price}`);
            }
          });
        });
      } else {
        // Orientation-based pricing (options format)
        this.steps.forEach(step => {
          if (!step.options) return;
          Object.keys(step.options).forEach(sectionKey => {
            if (sectionKey === 'installation') return;

            const sectionData = step.options[sectionKey];
            const nestedKeys = ['main', 'bath', 'fixture', 'accent', 'countertop', 'cabinets'];
            
            if (nestedKeys.some(k => sectionData[k])) {
              nestedKeys.forEach(subKey => {
                if (!sectionData[subKey]?.options) return;
                const configValue = this.currentConfig[sectionKey]?.[subKey];
                const selectedOption = sectionData[subKey].options.find(opt => opt.value === configValue);
                if (selectedOption?.price) additionalPrice += selectedOption.price;
              });
            } else if (sectionData.options) {
              const configValue = this.currentConfig[sectionKey];
              const selectedOption = sectionData.options.find(opt => opt.value === configValue);
              if (selectedOption?.price) additionalPrice += selectedOption.price;
            }
          });
        });
      }

      // Base product option price (excludes shipping/installation/permit)
      this.configPrice = additionalPrice;

      // Add installation + shipping costs from API (only after ZIP is known)
      let servicesPrice = 0;
      if (this.hasValidUserFormData()) {
        const apiShipping = normalizeNumber(this.finalPriceData?.shipping_cost, 0);
        const apiInstall = window.ConfiguratorShared.computeInstallationCost(
          this.finalPriceData?.breakdown?.installation,
          this.currentConfig?.installation
        );
        servicesPrice = apiShipping + apiInstall;
      }

      const totalPrice = this.basePrice + additionalPrice + servicesPrice + (this.permitPlanPrice || 0);

      log('Price calculation:', { base: this.basePrice, additional: additionalPrice, total: totalPrice });

      // Update ALL price elements (header and mobile footer)
      const priceEls = $$('[data-configurator-price]', this.step2Section);
      priceEls.forEach(el => el.textContent = formatPrice(totalPrice));

      // Update ALL monthly price elements
      const monthlyEls = $$('[data-monthly-price]', this.step2Section);
      monthlyEls.forEach(el => el.textContent = formatPrice(Math.round(totalPrice / 180)));

      // Only queue update if explicitly requested and not already in progress
      // This prevents infinite loops - queueFinalPriceUpdate will call updateTotalPrice with skipQueueUpdate=true
      // Also skip if we have valid price data for the current ZIP code
      const currentZip2 = this.userFormData?.zipCode;
      const hasValidPriceData2 = this.finalPriceData && this._finalPriceDataZip && 
                                this._finalPriceDataZip === currentZip2;
      if (!skipQueueUpdate && this.hasValidUserFormData() && !this._priceUpdateQueued && !hasValidPriceData2) {
        this._priceUpdateQueued = true;
        // Use requestAnimationFrame to defer the API call and prevent blocking
        requestAnimationFrame(() => {
          this._priceUpdateQueued = false;
          this.queueFinalPriceUpdate();
        });
      }
    } finally {
      this._updatingPrice = false;
    }
  }

  resolveTextureReference(textureValue) {
    if (!textureValue || typeof textureValue !== 'string') return textureValue;
    const trimmed = textureValue.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//')) return trimmed;
    return this.texturesData?.[trimmed] || trimmed;
  }

  // ============================================================================
  // SERVICES TAB
  // ============================================================================

  hasValidUserFormData() {
    return Boolean(
      this.userFormData &&
      this.userFormData.firstName &&
      this.userFormData.email &&
      this.userFormData.zipCode
    );
  }

  getModelSlug() {
    const modelName = this.modelsData?.name || this.modelConfig?.model?.name || this.modelConfig?.name || this.modelConfig?.model || 'portland';
    return window.ConfiguratorShared.canonicalModel(modelName);
  }

  buildFinalPricePayload(installationTypeOverride) {
    const zip = String(this.userFormData?.zipCode || '').trim();

    const pick = (...keys) => window.ConfiguratorShared.pickFirst(this.currentConfig, keys);

    // For panel-based models, dimensions are stored as:
    // - model.dimensions.width -> API depth (building width)
    // - model.dimensions.depth -> API length (building depth)
    const depth = normalizeNumber(
      this.modelsData?.depth ?? 
      this.modelConfig?.depth ?? 
      this.modelConfig?.model?.dimensions?.width, // width maps to API depth
      null
    );
    const length = normalizeNumber(
      this.modelsData?.length ?? 
      this.modelConfig?.length ?? 
      this.modelConfig?.model?.dimensions?.depth, // depth maps to API length
      null
    );

    log('[PortlandModal] buildFinalPricePayload dimensions:', {
      depth,
      length,
      modelsDataDepth: this.modelsData?.depth,
      modelsDataLength: this.modelsData?.length,
      modelConfigDepth: this.modelConfig?.depth,
      modelConfigLength: this.modelConfig?.length,
      modelDimensions: this.modelConfig?.model?.dimensions
    });

    return window.ConfiguratorShared.buildInstallationCostPayload({
      zip,
      model: this.getModelSlug(),
      depth,
      length,
      interior: pick('interior', 'interiorFinish', 'mainFloorFinish', 'flooring'),
      kitchen: pick('kitchen') || '',
      roofStyle: pick('roofStyle', 'roof', 'roofType'),
    });
  }

  async fetchFinalPrice(payload) {
    // DRY: moved to `assets/configurator-shared.js` installation cost client.
    return payload;
  }

  queueFinalPriceUpdate(servicesContainer) {
    if (!this.hasValidUserFormData()) return;

    if (!this.installationCostClient) {
      this.installationCostClient = window.ConfiguratorShared.createInstallationCostClient();
    }

    const selectedType = String(this.currentConfig.installation || 'shell');
    const depth = normalizeNumber(this.modelsData?.depth ?? this.modelConfig?.depth ?? this.modelConfig?.model?.dimensions?.depth, null);
    const length = normalizeNumber(this.modelsData?.length ?? this.modelConfig?.length ?? this.modelConfig?.model?.dimensions?.length, null);

    window.ConfiguratorShared.queueInstallationPricing({
      client: this.installationCostClient,
      zip: this.userFormData?.zipCode,
      modelRaw: this.getModelSlug(),
      depth,
      length,
      currentConfig: this.currentConfig,
      selectedType
    })
      .then((out) => {
        if (!out?.installation) return;
        this.finalPriceData = out.finalPriceData;

        window.ConfiguratorShared.applyInstallationCostsToServiceOptions({
          container: servicesContainer || this.step2Section,
          installation: out.installation,
          formatPrice,
          prefixPlus: false,
        });

        // Pass skipQueueUpdate=true to prevent infinite loop
        this.updateTotalPrice(true);

        // Notify only when we actually fetched new pricing (avoid spam on cached recalcs).
        if (out?.meta?.didFetch && out?.meta?.payloadKey) {
          if (this._lastInstallPricingToastKey !== out.meta.payloadKey) {
            this._lastInstallPricingToastKey = out.meta.payloadKey;
            window.ConfiguratorShared.showSuccess('Installation pricing updated.', 'Pricing');
          }
        }
      })
      .catch((e) => {
        const msg = e?.message || String(e || 'Installation pricing request failed');
        window.ConfiguratorShared.showError(msg, 'Installation pricing');
      });
  }

  applyFinalPriceToServicesUI(container) {
    if (!container || !this.finalPriceData) return;

    const install = this.finalPriceData?.breakdown?.installation;
    if (!install) return;

    window.ConfiguratorShared.applyInstallationCostsToServiceOptions({
      container,
      installation: install,
      formatPrice,
      prefixPlus: false,
    });
  }

  focusSaveDesignForm() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;
    const firstInput =
      contentWrapper.querySelector('[data-summary-form] input[name="firstName"]') ||
      contentWrapper.querySelector('[data-summary-form] input') ||
      null;
    if (firstInput) {
      firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInput.focus({ preventScroll: true });
    }
  }

  focusInstallationOptions() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;
    const installSection =
      contentWrapper.querySelector('[data-config-section="installation"]') ||
      contentWrapper.querySelector('[data-services-options]') ||
      null;
    if (installSection) installSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  getSaveDesignFormHTML() {
    return `
      <div class="config-section summary-form-view" data-save-design-form>
        <div class="text-center mb-md-5 mb-3 pe-xl-1">
          <h2 class="h3 mb-md-3 mb-2">You're almost done!</h2>
          <p class="mb-0">Your build is ready. Save your design by completing the form and get access to information about pricing, shipping, professional assembly, local permits, and financing options.</p>
        </div>
        <form class="summary-contact-form" data-summary-form>
          <div class="input-group mb-2"><input type="text" name="firstName" placeholder="First Name *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="text" name="lastName" placeholder="Last Name *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="email" name="email" placeholder="Email *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="text" name="zipCode" placeholder="ZIP code *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="tel" name="phone" placeholder="Phone Number" class="form-control"></div>
          <p class="form-disclaimer mb-xl-13 mb-md-7 mb-4">By providing a telephone number and when scheduling your consultation you are consenting to be contacted by SMS text message. Message & data rates may apply. You can reply STOP to opt-out of further messaging.</p>
          <button type="submit" class="visually-hidden" aria-hidden="true" tabindex="-1">Submit</button>
        </form>
      </div>
    `;
  }

  // attachSaveDesignFormHandlers(container) {
  //   const form = $('[data-summary-form]', container);
  //   if (!form) return;

  //   // Create and attach form validator for real-time validation
  //   const validator = window.ConfiguratorShared?.createFormValidator?.(form);
  //   if (validator) {
  //     validator.attachListeners();
  //     this._formValidator = validator;
  //   }

  //   form.addEventListener('submit', async (e) => {
  //     e.preventDefault();
      
  //     // Validate form using our custom validator first
  //     if (this._formValidator) {
  //       const { valid, firstInvalid } = this._formValidator.validateAll();
  //       if (!valid) {
  //         firstInvalid?.focus();
  //         firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  //         return;
  //       }
  //     }
      
  //     // Prevent double submission
  //     if (this._isValidatingZip) return;
      
  //     const formData = new FormData(form);
  //     const pendingUserData = {
  //       firstName: String(formData.get('firstName') || ''),
  //       lastName: String(formData.get('lastName') || ''),
  //       email: String(formData.get('email') || ''),
  //       zipCode: String(formData.get('zipCode') || ''),
  //       phone: String(formData.get('phone') || '')
  //     };

  //     // Show loading state on submit button
  //     const submitBtn = form.querySelector('[data-next-step], button[type="submit"], .next-step-btn');
  //     const footerBtn = $('[data-next-step]', this.step2Section);
  //     const btnToDisable = submitBtn || footerBtn;
  //     const nextBtnTextSpan = $('[data-next-step-text]', this.step2Section);
  //     const originalBtnText = nextBtnTextSpan?.textContent || 'SAVE DESIGN';
      
  //     try {
  //       this._isValidatingZip = true;
  //       if (btnToDisable) {
  //         btnToDisable.disabled = true;
  //       }
  //       // Show "Loading..." text on button (similar to Design Your Own button)
  //       if (nextBtnTextSpan) {
  //         nextBtnTextSpan.textContent = 'Loading...';
  //       }

  //       // Build payload for validation
  //       const depth = normalizeNumber(this.modelsData?.depth ?? this.modelConfig?.depth ?? this.modelConfig?.model?.dimensions?.depth, null);
  //       const length = normalizeNumber(this.modelsData?.length ?? this.modelConfig?.length ?? this.modelConfig?.model?.dimensions?.length, null);
  //       const pick = (...keys) => window.ConfiguratorShared.pickFirst(this.currentConfig, keys);

  //       // CRITICAL: Validate ZIP code with API BEFORE allowing step progression
  //       const result = await window.ConfiguratorShared.validateZipCodeWithAPI({
  //         zip: pendingUserData.zipCode,
  //         model: this.getModelSlug(),
  //         depth,
  //         length,
  //         interior: pick('interior', 'interiorFinish', 'mainFloorFinish', 'flooring'),
  //         kitchen: pick('kitchen') || '',
  //         roofStyle: pick('roofStyle', 'roof', 'roofType'),
  //         base_product_price: this.basePrice + this.configPrice,
  //       });

  //       // API returned 200 and valid data - NOW we can proceed
  //       this.userFormData = pendingUserData;
  //       window.studioShedUserData = this.userFormData;
        
  //       // Store the validated API data (new format from final-price API)
  //       if (result?.data) {
  //         const apiData = result.data;
  //         const breakdownInstallation = apiData.breakdown?.installation || {};
  //         const breakdownShipping = apiData.breakdown?.shipping || {};
  //         const selectedType = String(this.currentConfig.installation || 'shell');
          
  //         // Build installation object with prices from breakdown
  //         const installationWithPrices = {
  //           shell: normalizeNumber(breakdownInstallation.shell, 0),
  //           lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
  //           kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
  //           selected_type: breakdownInstallation.selected_type || selectedType,
  //           selected_cost: normalizeNumber(breakdownInstallation.selected_cost || apiData.installation_cost, 0),
  //           state_code: breakdownInstallation.state_code,
  //           zone: breakdownInstallation.zone,
  //         };
          
  //         this.finalPriceData = {
  //           breakdown: { 
  //             installation: installationWithPrices,
  //             installationPrices: {
  //               shell: normalizeNumber(breakdownInstallation.shell, 0),
  //               lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
  //               kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
  //             },
  //             shipping: {
  //               base_subtotal: normalizeNumber(breakdownShipping.base_subtotal, 0),
  //               depth_surcharge: normalizeNumber(breakdownShipping.depth_surcharge, 0),
  //               interior_surcharge: normalizeNumber(breakdownShipping.interior_surcharge, 0),
  //               inflation_surcharge: normalizeNumber(breakdownShipping.inflation_surcharge, 0),
  //             },
  //           },
  //           installation_cost: normalizeNumber(apiData.installation_cost, 0),
  //           shipping_cost: normalizeNumber(apiData.shipping_cost, 0),
  //           total_price: normalizeNumber(apiData.total_price, 0),
  //           product_price: normalizeNumber(apiData.product_price, 0),
  //           city: apiData.city || '',
  //           state_code: apiData.state_code || '',
  //         };
  //         // Track the ZIP code used to fetch this data to prevent duplicate API calls
  //         // Only call API again if ZIP code changes
  //         this._finalPriceDataZip = pendingUserData.zipCode;

  //         // Note: applyInstallationCostsToServiceOptions is called in populateServicesOptions
  //         // after the service option elements are created in the DOM
  //       }

  //       // Call Save Design API (non-blocking - don't wait for it)
  //       // This runs in parallel with the UI update
  //       window.ConfiguratorShared.saveDesignToAPI(this, pendingUserData)
  //         .then((saveResult) => {
  //           if (saveResult?.success) {
  //             console.log('[Portland] Design saved successfully:', saveResult.design_id);
  //           } else if (saveResult?.error) {
  //             console.warn('[Portland] Save Design API:', saveResult.error);
  //           }
  //         })
  //         .catch((error) => {
  //           // Log error but don't block user flow
  //           console.error('[Portland] Save Design API error:', error);
  //         });

  //       // SUCCESS: Show installation options (pass true to show options instead of form)
  //       this.populateServicesOptions(true);
        
  //       setTimeout(() => {
  //         this.focusInstallationOptions();
  //         // Update total price after switching tabs
  //         this.updateTotalPrice(true);
  //       }, 50);
        
  //     } catch (error) {
  //       // FAILURE: Stay on current step and show error
  //       const errorMessage = error?.message || 'ZIP code validation failed. Please try again.';
  //       window.ConfiguratorShared.showError(errorMessage, 'Invalid ZIP Code');
        
  //       // Show contact link below the Save Design button
  //       window.ConfiguratorShared.showContactLinkOnError(this.step2Section);
        
  //       // Focus the ZIP code input for correction
  //       const zipInput = form.querySelector('input[name="zipCode"]');
  //       if (zipInput) {
  //         zipInput.focus();
  //         zipInput.select();
  //       }
        
  //       // Do NOT switch tabs - stay on current step
  //       // Restore button text only on error (success case will have already switched to installation options with SUMMARY)
  //       if (btnToDisable) {
  //         btnToDisable.disabled = false;
  //       }
  //       // Also re-enable the footer button if it's different from btnToDisable
  //       if (footerBtn && footerBtn !== btnToDisable) {
  //         footerBtn.disabled = false;
  //       }
  //       // Also re-enable submit button if it's different
  //       if (submitBtn && submitBtn !== btnToDisable) {
  //         submitBtn.disabled = false;
  //       }
  //       // Restore the span text, not the entire button (to preserve button structure with arrow)
  //       if (nextBtnTextSpan) {
  //         nextBtnTextSpan.textContent = originalBtnText;
  //       }
  //     } finally {
  //       this._isValidatingZip = false;
  //       // Re-enable ALL buttons that might have been disabled
  //       if (btnToDisable) {
  //         btnToDisable.disabled = false;
  //       }
  //       if (footerBtn && footerBtn !== btnToDisable) {
  //         footerBtn.disabled = false;
  //       }
  //       if (submitBtn && submitBtn !== btnToDisable) {
  //         submitBtn.disabled = false;
  //       }
  //     }
  //   });
  // }

  attachSaveDesignFormHandlers(container) {
    const form = $('[data-summary-form]', container);
    if (!form) return;

    // Check if handler is already attached to this form to prevent duplicates
    if (form.dataset.handlerAttached === 'true') {
      console.log('[Portland] Form submit handler already attached, skipping', {
        timestamp: new Date().toISOString()
      });
      return;
    }

    // Create and attach form validator for real-time validation
    const validator = window.ConfiguratorShared?.createFormValidator?.(form);
    if (validator) {
      validator.attachListeners();
      this._formValidator = validator;
    }

    // Remove existing listener if any to prevent duplicates (in case form was reused)
    if (this._saveDesignFormSubmitHandler) {
      form.removeEventListener('submit', this._saveDesignFormSubmitHandler);
    }

    // Create named handler function so we can remove it later
    this._saveDesignFormSubmitHandler = async (e) => {
      e.preventDefault();
      
      // Validate form using our custom validator first
      if (this._formValidator) {
        const { valid, firstInvalid } = this._formValidator.validateAll();
        if (!valid) {
          firstInvalid?.focus();
          firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      
      // Generate unique request ID for this submission
      const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // GLOBAL LOCK: Use window-level lock to prevent duplicate submissions across all instances
      // This catches cases where multiple form instances or event handlers trigger simultaneously
      if (!window._portlandFormSubmissionLock) {
        window._portlandFormSubmissionLock = { locked: false, timestamp: 0 };
      }
      
      const now = Date.now();
      // If locked and lock is less than 10 seconds old, block this submission
      if (window._portlandFormSubmissionLock.locked && (now - window._portlandFormSubmissionLock.timestamp) < 10000) {
        console.log('[Portland] Form submission BLOCKED by GLOBAL LOCK', {
          requestId,
          timestamp: new Date().toISOString(),
          lockAge: now - window._portlandFormSubmissionLock.timestamp
        });
        return;
      }
      
      // Acquire global lock IMMEDIATELY (synchronous)
      window._portlandFormSubmissionLock.locked = true;
      window._portlandFormSubmissionLock.timestamp = now;
      
      // Initialize instance flags if undefined (safety check)
      if (this._isValidatingZip === undefined) this._isValidatingZip = false;
      if (this._isSavingDesign === undefined) this._isSavingDesign = false;
      
      // INSTANCE CHECK: Also check instance-level flags
      if (this._isValidatingZip === true || this._isSavingDesign === true) {
        console.log('[Portland] Form submission BLOCKED - instance already in progress', {
          requestId,
          timestamp: new Date().toISOString(),
          isValidatingZip: this._isValidatingZip,
          isSavingDesign: this._isSavingDesign
        });
        // Release global lock since we're not proceeding
        window._portlandFormSubmissionLock.locked = false;
        return;
      }
      
      // Set instance flags IMMEDIATELY
      this._isValidatingZip = true;
      this._isSavingDesign = true;
      
      console.log('[Portland] Form submit handler called', {
        requestId,
        timestamp: new Date().toISOString(),
        isValidatingZip: this._isValidatingZip,
        isSavingDesign: this._isSavingDesign,
        stackTrace: new Error().stack
      });
      
      const formData = new FormData(form);
      const pendingUserData = {
        firstName: String(formData.get('firstName') || ''),
        lastName: String(formData.get('lastName') || ''),
        email: String(formData.get('email') || ''),
        zipCode: String(formData.get('zipCode') || ''),
        phone: String(formData.get('phone') || '')
      };

      // Show loading state on submit button
      const submitBtn = form.querySelector('[data-next-step], button[type="submit"], .next-step-btn');
      const footerBtn = $('[data-next-step]', this.step2Section);
      const btnToDisable = submitBtn || footerBtn;
      const nextBtnTextSpan = $('[data-next-step-text]', this.step2Section);
      const originalBtnText = nextBtnTextSpan?.textContent || 'SAVE DESIGN';
      
      try {
        // Flag already set above to prevent race conditions
        if (btnToDisable) {
          btnToDisable.disabled = true;
        }
        // Show "Loading..." text on button (similar to Design Your Own button)
        if (nextBtnTextSpan) {
          nextBtnTextSpan.textContent = 'Loading...';
        }

        // Build payload for validation
        const depth = normalizeNumber(this.modelsData?.depth ?? this.modelConfig?.depth ?? this.modelConfig?.model?.dimensions?.depth, null);
        const length = normalizeNumber(this.modelsData?.length ?? this.modelConfig?.length ?? this.modelConfig?.model?.dimensions?.length, null);
        const pick = (...keys) => window.ConfiguratorShared.pickFirst(this.currentConfig, keys);

        // CRITICAL: Validate ZIP code with API BEFORE allowing step progression
        const result = await window.ConfiguratorShared.validateZipCodeWithAPI({
          zip: pendingUserData.zipCode,
          model: this.getModelSlug(),
          depth,
          length,
          interior: pick('interior', 'interiorFinish', 'mainFloorFinish', 'flooring'),
          kitchen: pick('kitchen') || '',
          roofStyle: pick('roofStyle', 'roof', 'roofType'),
          base_product_price: this.basePrice + this.configPrice,
        });

        // API returned 200 and valid data - NOW we can proceed
        this.userFormData = pendingUserData;
        window.studioShedUserData = this.userFormData;
        
        // Store the validated API data (new format from final-price API)
        if (result?.data) {
          const apiData = result.data;
          const breakdownInstallation = apiData.breakdown?.installation || {};
          const breakdownShipping = apiData.breakdown?.shipping || {};
          const selectedType = String(this.currentConfig.installation || 'shell');
          
          // Build installation object with prices from breakdown
          const installationWithPrices = {
            shell: normalizeNumber(breakdownInstallation.shell, 0),
            lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
            kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
            selected_type: breakdownInstallation.selected_type || selectedType,
            selected_cost: normalizeNumber(breakdownInstallation.selected_cost || apiData.installation_cost, 0),
            state_code: breakdownInstallation.state_code,
            zone: breakdownInstallation.zone,
          };
          
          this.finalPriceData = {
            breakdown: { 
              installation: installationWithPrices,
              installationPrices: {
                shell: normalizeNumber(breakdownInstallation.shell, 0),
                lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
                kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
              },
              shipping: {
                base_subtotal: normalizeNumber(breakdownShipping.base_subtotal, 0),
                depth_surcharge: normalizeNumber(breakdownShipping.depth_surcharge, 0),
                interior_surcharge: normalizeNumber(breakdownShipping.interior_surcharge, 0),
                inflation_surcharge: normalizeNumber(breakdownShipping.inflation_surcharge, 0),
              },
            },
            installation_cost: normalizeNumber(apiData.installation_cost, 0),
            shipping_cost: normalizeNumber(apiData.shipping_cost, 0),
            total_price: normalizeNumber(apiData.total_price, 0),
            product_price: normalizeNumber(apiData.product_price, 0),
            city: apiData.city || '',
            state_code: apiData.state_code || '',
          };
          // Track the ZIP code used to fetch this data to prevent duplicate API calls
          // Only call API again if ZIP code changes
          this._finalPriceDataZip = pendingUserData.zipCode;

          // Apply installation costs to service options directly (no need to call API again)
          window.ConfiguratorShared.applyInstallationCostsToServiceOptions({
            container: this.step2Section,
            installation: installationWithPrices,
            formatPrice: (val) => window.ConfiguratorShared.formatPriceSmart(val, { noDecimals: true }),
            prefixPlus: false,
          });
        }

        // Call Save Design API (non-blocking - don't wait for it)
        // Double-check flag before calling API (extra safety)
        if (!this._isSavingDesign) {
          console.warn('[Portland] WARNING: _isSavingDesign flag is false but should be true!', {
            requestId,
            timestamp: new Date().toISOString()
          });
          // Set it again just in case
          this._isSavingDesign = true;
        }
        
        console.log('[Portland] Calling saveDesignToAPI', {
          requestId,
          timestamp: new Date().toISOString(),
          userData: { email: pendingUserData.email, zipCode: pendingUserData.zipCode },
          isSavingDesign: this._isSavingDesign,
          formDataHash: `${pendingUserData.email}_${pendingUserData.zipCode}_${Date.now()}`
        });
        
        window.ConfiguratorShared.saveDesignToAPI(this, pendingUserData, { requestId })
            .then((saveResult) => {
              console.log('[Portland] saveDesignToAPI response:', {
                requestId,
                timestamp: new Date().toISOString(),
                success: saveResult?.success,
                design_id: saveResult?.design_id,
                error: saveResult?.error
              });
              if (saveResult?.success) {
                console.log('[Portland] Design saved successfully:', saveResult.design_id);
              } else if (saveResult?.error) {
                console.warn('[Portland] Save Design API:', saveResult.error);
              }
            })
            .catch((error) => {
              // Log error but don't block user flow
              console.error('[Portland] Save Design API error:', {
                requestId,
                timestamp: new Date().toISOString(),
                error: error.message,
                stack: error.stack
              });
            })
            .finally(() => {
              // Always clear the flag when done (success or error)
              console.log('[Portland] Clearing _isSavingDesign flag', {
                requestId,
                timestamp: new Date().toISOString()
              });
              this._isSavingDesign = false;
            });

        // SUCCESS: Show installation options (pass true to show options instead of form)
        this.populateServicesOptions(true);
        
        setTimeout(() => {
          this.focusInstallationOptions();
          // Update total price after switching tabs
          this.updateTotalPrice(true);
        }, 50);
        
      } catch (error) {
        // FAILURE: Stay on current step and show error
        const errorMessage = error?.message || 'ZIP code validation failed. Please try again.';
        window.ConfiguratorShared.showError(errorMessage, 'Invalid ZIP Code');
        
        // Show contact link below the Save Design button
        window.ConfiguratorShared.showContactLinkOnError(this.step2Section);
        
        // Focus the ZIP code input for correction
        const zipInput = form.querySelector('input[name="zipCode"]');
        if (zipInput) {
          zipInput.focus();
          zipInput.select();
        }
        
        // Do NOT switch tabs - stay on current step
        // Restore button text only on error (success case will have already switched to installation options with SUMMARY)
        if (btnToDisable) {
          btnToDisable.disabled = false;
        }
        // Also re-enable the footer button if it's different from btnToDisable
        if (footerBtn && footerBtn !== btnToDisable) {
          footerBtn.disabled = false;
        }
        // Also re-enable submit button if it's different
        if (submitBtn && submitBtn !== btnToDisable) {
          submitBtn.disabled = false;
        }
        // Restore the span text, not the entire button (to preserve button structure with arrow)
        if (nextBtnTextSpan) {
          nextBtnTextSpan.textContent = originalBtnText;
        }
      } finally {
        this._isValidatingZip = false;
        this._isSavingDesign = false;
        // Release global lock
        if (window._portlandFormSubmissionLock) {
          window._portlandFormSubmissionLock.locked = false;
        }
        // Re-enable ALL buttons that might have been disabled
        if (btnToDisable) {
          btnToDisable.disabled = false;
        }
        if (footerBtn && footerBtn !== btnToDisable) {
          footerBtn.disabled = false;
        }
        if (submitBtn && submitBtn !== btnToDisable) {
          submitBtn.disabled = false;
        }
        console.log('[Portland] Form submission complete, flags reset', {
          timestamp: new Date().toISOString(),
          isValidatingZip: this._isValidatingZip,
          isSavingDesign: this._isSavingDesign
        });
      }
    };

    // Attach the handler
    form.addEventListener('submit', this._saveDesignFormSubmitHandler);
    form.dataset.handlerAttached = 'true'; // Mark as attached to prevent duplicates
    console.log('[Portland] Form submit handler attached', {
      timestamp: new Date().toISOString(),
      formId: form.id || 'no-id',
      formElement: form
    });
  }

  populateServicesOptions(showInstallationOptions = false) {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    contentWrapper.innerHTML = '';

    const panelContent = document.createElement('div');
    panelContent.className = 'panel-content';
    panelContent.dataset.tabContent = 'services';

    // Show installation options if:
    // 1. Explicitly requested (after form submission), OR
    // 2. User already has valid form data and price data (returning to Services tab)
    const hasValidData = this.hasValidUserFormData() && this.finalPriceData;
    if (showInstallationOptions || hasValidData) {
      if (this.hasValidUserFormData()) {
        console.log('[Portland] populateServicesOptions - Showing installation options');
        this.showInstallationOptionsContent(contentWrapper, panelContent);
        return;
      }
    }

    // Clear any stale data and show the form
    console.log('[Portland] populateServicesOptions - Showing form');
    this.userFormData = null;
    window.studioShedUserData = null;
    this.finalPriceData = null;
    this._finalPriceDataZip = null;

    // Show the form
    panelContent.innerHTML = this.getSaveDesignFormHTML();
    contentWrapper.appendChild(panelContent);
    this.attachSaveDesignFormHandlers(panelContent);

    const nextBtnText = $('[data-next-step-text]', this.step2Section);
    if (nextBtnText) nextBtnText.textContent = 'SAVE DESIGN';
    const backBtn = $('[data-back-step]', this.step2Section);
    if (backBtn) backBtn.style.display = '';
    
    // Disable Summary tab when form is shown (not yet valid)
    this.updateSummaryTabState();

    // Hide header Save Design button when form is shown (only show footer SAVE DESIGN button)
    const headerSaveBtn = $('[data-save-design]', this.step2Section);
    if (headerSaveBtn) headerSaveBtn.style.display = 'none';

    this.updateTotalPrice(true);
  }

  showInstallationOptionsContent(contentWrapper, panelContent) {    let installationOptions = this.modelConfig?.services?.installation?.options || [];
    installationOptions = [...installationOptions].sort((a, b) => {
      const valA = (a.value || '').toLowerCase();
      const valB = (b.value || '').toLowerCase();
      
      const getOrder = (val) => {
        if (val.includes('lifestyle')) return 1;
        if (val === 'shell' || val.includes('shell')) return 2;
        if (val.includes('product') || val.includes('diy') || val === 'none') return 3;
        return 99;
      };
      
      return getOrder(valA) - getOrder(valB);
    });

    const rawInteriorValue = String(
      this.currentConfig.interiorOptions || 
      this.currentConfig.interiorPackage || 
      this.currentConfig.layout || 
      this.currentConfig.interior ||
      window.ConfiguratorShared?.pickFirst?.(this.currentConfig, ['interiorFinish', 'mainFloorFinish', 'flooring']) || 
      ''
    ).toLowerCase().replace(/[\s_-]/g, '');

    const isShellOnly = rawInteriorValue.includes('shellonly') || rawInteriorValue === 'shell' || rawInteriorValue === 'none';

    if (isShellOnly) {
      installationOptions = installationOptions.filter(o => !/lifestyle/i.test(o.value));
    }

    let defaultVal;
    if (isShellOnly) {
      // For shell-only interiors, look for "shell-only" or "shell" installation options
      defaultVal = installationOptions.find(o => o.value === 'shell-only' || o.value === 'shell')?.value;
    } else {
      // For lifestyle interiors, look for "lifestyle" options
      defaultVal = installationOptions.find(o => /lifestyle/i.test(o.value))?.value;
    }
    
    if (!defaultVal) {
      defaultVal = installationOptions.find(o => o.isDefault)?.value || installationOptions[0]?.value;
    }

    const currentValue = this.currentConfig.installation || defaultVal;
    if (currentValue) this.currentConfig.installation = currentValue;

    panelContent.innerHTML = `
      <div class="config-section services-section" data-config-section="installation">
        <p class="services-header fw-medium mb-1" data-services-header>Installation</p>
        <div class="services-options" data-services-options>
          ${installationOptions.map(option => this.renderServiceOption(option, option.value === currentValue)).join('')}
        </div>
      </div>
    `;

    contentWrapper.appendChild(panelContent);

    qa('[data-service-option]', '.service-option-card', panelContent).forEach(card => {
      card.addEventListener('click', () => {
        const { section, value } = card.dataset;
        
        qa('[data-service-option]', '.service-option-card', card.closest('[data-services-options], .services-options')).forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        this.currentConfig[section] = value;
        
        this.updateTotalPrice(true);
        
        if (typeof this.recalculateSummaryTotal === 'function') {
          this.recalculateSummaryTotal();
        }
      });
    });

    this.currentStep = this.steps.length + 1;
    const nextBtnText = $('[data-next-step-text]', this.step2Section);
    if (nextBtnText) nextBtnText.textContent = 'Summary';
    
    const backBtn = $('[data-back-step]', this.step2Section);
    if (backBtn) backBtn.style.display = '';

    const headerSaveBtn = $('[data-save-design]', this.step2Section);
    if (headerSaveBtn) {
      headerSaveBtn.style.display = '';
      headerSaveBtn.textContent = 'START OVER';
      headerSaveBtn.dataset.startOver = 'true';
    }
    
    // Pass skipQueueUpdate=true to prevent another API call - we already have the data
    this.updateTotalPrice(true);
    this.updateSummaryTabState();

    // Apply the installation prices from the API response
    const installation = this.finalPriceData?.breakdown?.installation;
    if (installation) {
      window.ConfiguratorShared.applyInstallationCostsToServiceOptions({
        container: panelContent,
        installation: installation,
        formatPrice: (val) => window.ConfiguratorShared.formatPriceSmart(val, { noDecimals: true }),
        prefixPlus: false,
      });
    }
  }

  renderServiceOption(option, isSelected) {
    const priceDisplay = option.price > 0 ? `<span class="service-price" data-service-price style="letter-spacing: -0.02em;">${formatPrice(option.price)}</span>` : '';
    const noteHTML = option.note ? `<p class="fs-sm fw-medium mt-4 mb-0" data-service-note>${option.note}</p>` : '';
    return `
      <div class="service-option-card${isSelected ? ' selected' : ''}" data-service-option data-section="installation" data-value="${option.value}" data-display-name="${option.displayName}" data-price="${option.price || 0}">
        <div class="service-option-header d-flex align-items-center justify-content-between mb-2" data-service-header>
          <p class="service-option-title fw-medium mb-0" data-service-title style="letter-spacing: -0.02em;">${option.displayName}</p>
          ${priceDisplay}
          <span class="aspect-variant-check" data-service-checkmark>
            <svg viewBox="0 0 8 7" fill="none" style="width: 11px; height: 6px; margin-bottom: -1px;">
              <path d="M0.5 3.35714L2.77027 5.5L7.5 0.5" stroke="currentColor" stroke-linecap="round"></path>
            </svg>
          </span>
        </div>
        <p class="fs-sm mb-0" data-service-description style="letter-spacing: -0.01em;">${option.description}</p>
        ${noteHTML}
      </div>
    `;
  }

  // ============================================================================
  // SUMMARY TAB
  // ============================================================================

  showSummary() {
    // Summary tab should be disabled when form is invalid.
    // Do NOT redirect - just prevent display if somehow accessed.
    if (!this.hasValidUserFormData()) {
      // Ensure Summary tab is disabled
      this.updateSummaryTabState();
      return;
    }

    this.showThankYouSummary();

    this.currentStep = this.steps.length + 2;

    const panelFooter = q('[data-panel-footer]', '.panel-footer', this.step2Section);
    if (panelFooter) panelFooter.style.display = 'none';

    const saveDesignBtn = $('[data-save-design]', this.step2Section);
    if (saveDesignBtn) {
      saveDesignBtn.textContent = 'START OVER';
      saveDesignBtn.dataset.startOver = 'true';
    }

    // Add product-summary-tab class to data-configurator-main element (Portland-specific)
    const configuratorMain = $('[data-configurator-main]', this.step2Section);
    if (configuratorMain) {
      configuratorMain.classList.add('product-summary-tab');
    }
  }

  showThankYouSummary() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    // Change layout to 6-6 columns on thank you page
    const viewer = $('.configurator-viewer', this.step2Section);
    const panel = $('.configurator-panel', this.step2Section);
    if (viewer) {
      viewer.classList.remove('col-lg-8');
      viewer.classList.add('col-lg-6');
      const modelContainer = $('[data-model-viewer], .model-viewer-container', viewer);
      if (modelContainer) {
        modelContainer.style.setProperty('max-width', '100%', 'important');
        modelContainer.style.setProperty('margin', '0 auto', 'important');
      }
    }
    if (panel) {
      panel.classList.remove('col-lg-4');
      panel.classList.add('col-lg-6');
    }

    // Trigger 3D viewer resize after layout change
    setTimeout(() => {
      if (this.viewer3D?.sceneManager?.onResize) {
        this.viewer3D.sceneManager.onResize();
      }
    }, 50);

    contentWrapper.innerHTML = '';

    const panelContent = document.createElement('div');
    panelContent.className = 'panel-content summary-panel';
    panelContent.dataset.tabContent = 'summary';
    panelContent.innerHTML = this.buildThankYouHTML();
    contentWrapper.appendChild(panelContent);

    this.setupThankYouEventListeners(panelContent);
  }

  buildThankYouHTML() {
    const variantTitle = this.selectedVariant?.title || this.modelsData?.name || '';
    const orientation = this.currentConfig.orientation;
    const productPrice = this.basePrice + this.configPrice;
    
    const installationOptions = this.modelConfig?.services?.installation?.options || [];
    const selectedInstallation = installationOptions.find(opt => opt.value === this.currentConfig.installation);
    
    // Get installation price from API breakdown
    // For "shell-lifestyle" option, use lifestyle price from API breakdown
    let installationPrice = 0;
    const installationValue = this.currentConfig.installation || '';
    
    if (this.finalPriceData?.breakdown?.installation) {
      const breakdown = this.finalPriceData.breakdown.installation;
      
      // If user selected "shell-lifestyle", use lifestyle price from API breakdown
      if (installationValue.toLowerCase().includes('lifestyle')) {
        installationPrice = normalizeNumber(breakdown.lifestyle, 0);
      } else {
        // For other options, use the computed installation cost
        const computedCents = window.ConfiguratorShared.computeInstallationCost(
          breakdown,
          installationValue
        );
        installationPrice = computedCents > 0 ? computedCents : normalizeNumber(selectedInstallation?.price, 0);
      }
    } else {
      // Fallback if no API data available
      installationPrice = normalizeNumber(selectedInstallation?.price, 0);
    }
    
    const installationName = selectedInstallation?.displayName || '';
    
    const shippingPrice = normalizeNumber(this.finalPriceData?.shipping_cost, this.modelConfig?.shippingPrice || 0);
    const estimatedTotal = productPrice + shippingPrice + this.permitPlanPrice + installationPrice;
    const zipCode = this.userFormData?.zipCode || '';
    const configSummary = this.buildConfigSummaryString();

    // Build product title with optional orientation
    const productTitleParts = [variantTitle];
    if (orientation) productTitleParts.push(`(${formatOptionValue(orientation)})`);
    const productTitle = productTitleParts.filter(Boolean).join(' ');

    // Build shipping label with city and state_code from API
    const city = this.finalPriceData?.city || '';
    const stateCode = this.finalPriceData?.state_code || '';
    const shippingLocation = this.modelConfig?.shippingLocation || '';
    
    let shippingLabel = 'Shipping';
    if (city && stateCode && zipCode) {
      shippingLabel = `Shipping to ${city}, ${stateCode} (${zipCode})`;
    } else if (city && stateCode) {
      shippingLabel = `Shipping to ${city}, ${stateCode}`;
    } else if (shippingLocation && zipCode) {
      shippingLabel = `Shipping to ${shippingLocation} (${zipCode})`;
    } else if (zipCode) {
      shippingLabel = `Shipping (${zipCode})`;
    }

    return `
      <div class="summary-content summary-thankyou">
        <div class="thank-you-header-box mb-md-6 mb-4">
          <h2 class="h3 mb-3 thank-you-title">Thank you!</h2>
          <p class="thank-you-description mb-0" style="letter-spacing: -0.02em;">Your order summary has been emailed, and a dedicated team member will contact you shortly to guide you through the next steps and help you understand the entire process, ensuring everything is handled seamlessly.</p>
        </div>
        
        <div class="summary-product-section mb-2">
          <div class="d-flex align-items-end justify-content-between mb-2">
            <h3 class="h4 mb-0 summary-product-title">${productTitle}</h3>
            <span class="fw-medium" style="line-height: 1.4; letter-spacing: -0.02em;">${formatPrice(productPrice)}</span>
          </div>
          ${configSummary ? `
          <div class="d-flex align-items-center justify-content-between">
            <p class="fs-sm text-gray-600 mb-0" style="letter-spacing: -0.01em;">${configSummary}</p>
            <a href="#" class="text-decoration-underline" data-edit-design>Edit Design</a>
          </div>
          ` : ''}
        </div>
        
        <div class="summary-price-details-box mb-md-4 mb-3" data-price-details-box>
          <button class="price-details-toggle d-flex align-items-center justify-content-between" data-toggle-price-details>
            <span>Price details</span>
            <span class="d-flex align-items-center" data-toggle-icon>
              <svg aria-hidden="true" focusable="false" style="width:15px; height:15px;">
                <use href="#icon-modal-plus"></use>
              </svg>
            </span>
          </button>
          <div data-price-details-content>
            <div class="price-details-content">${this.buildPriceDetailsHTML()}</div>
          </div>
        </div>
        
        <div class="summary-line-items" data-summary-line-items>
          <div class="summary-line-item d-flex align-items-center justify-content-between gap-2 py-md-4 py-3" data-summary-line-item="shipping">
            <span data-line-item-label>${shippingLabel}</span>
            <span class="fw-medium" data-line-item-price>${shippingPrice > 0 ? formatPrice(shippingPrice) : 'Included'}</span>
          </div>
          ${installationPrice > 0 && installationName ? `
          <div class="summary-line-item d-flex align-items-center justify-content-between gap-2 py-md-4 py-3" data-summary-line-item="installation">
            <span class="line-item-label" data-line-item-label>${installationName} <span class="line-item-note" data-line-item-note>(subject to availability)</span></span>
            <span class="line-item-price fw-medium" data-line-item-price>${formatPrice(installationPrice)}</span>
          </div>` : ''}
          <div class="summary-line-item py-md-4 py-3" data-summary-line-item="permit">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2" data-line-item-main>
              <span data-line-item-label>${this.permitPlanSelected === 'include' ? 'Include Permit Plans' : 'No Permit Plans'}</span>
              <span class="fw-medium" data-permit-price-display>${this.permitPlanPrice > 0 ? formatPrice(this.permitPlanPrice) : 'No charge'}</span>
            </div>
            <button class="bg-transparent border-0 fw-light text-decoration-underline p-0" style="letter-spacing: -0.02em;" data-change-permit-plans>Change permit plans</button>
          </div>
          <div class="summary-line-item py-md-4 py-3" data-summary-line-item="foundation">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2" data-line-item-main>
              <span class="line-item-label" data-foundation-label>Foundation TBD</span>
              <span class="fw-medium" data-line-item-price>Cost not included</span>
            </div>
            <button class="bg-transparent border-0 fw-light text-decoration-underline p-0" style="letter-spacing: -0.02em;" data-change-foundation-type>Change foundation type</button>
          </div>
        </div>
        
        <div class="pt-lg-4 pb-lg-5 py-3" data-summary-total>
          <div class="d-flex justify-content-between mb-2" data-summary-total-row>
            <h5 class="mb-0" data-total-label>Estimated Total</h5>
            <h5 class="mb-0" data-summary-total-price>${formatPrice(estimatedTotal)}</h5>
          </div>
          <p class="fs-sm mb-0" data-total-note style="letter-spacing: -0.01em;">${this.getTotalNoteText()}</p>
        </div>
        
        ${this.buildCTASectionHTML()}
        ${this.buildDesignDetailsHTML(variantTitle)}
      </div>
    `;
  }

  buildConfigSummaryString() {
    const parts = [];
    // Add all selected config values that exist
    const summaryKeys = ['layout', 'kitchenConfig', 'kitchen', 'exterior', 'windowOption', 'roofType', 'roof'];
    summaryKeys.forEach(key => {
      const value = this.currentConfig[key];
      if (value && !parts.includes(formatOptionValue(value))) {
        parts.push(formatOptionValue(value));
      }
    });
    return parts.join(' | ');
  }

  buildPriceDetailsHTML() {
    const variantTitle = this.selectedVariant?.title || this.modelsData?.name || '';
    const sqFt = this.selectedVariant?.sqFt || this.modelsData?.squareFootage || '';
    
    // Build features from actual config values
    const configFeatures = [
      this.currentConfig.layout,
      this.currentConfig.exterior || this.currentConfig.windowConfig,
      this.currentConfig.roof || this.currentConfig.roofType
    ].filter(Boolean).map(formatOptionValue);

    // Build label with optional sqFt
    const labelParts = [variantTitle];
    if (sqFt) labelParts.push(`(${sqFt} sq. ft.)`);
    const mainLabel = labelParts.filter(Boolean).join(' ');

    let html = '';
    
    // Only show main section if we have a label
    if (mainLabel) {
      html += `
        <div class="price-detail-item price-detail-main">
          <div class="price-detail-info">
            <span class="d-inline-block mb-2">${mainLabel}</span>
            ${configFeatures.map(f => `<span class="price-detail-feature fs-sm text-gray-600 mb-1 d-block" style="letter-spacing: -0.01em;">${f}</span>`).join('')}
          </div>
          <span>${formatPrice(this.basePrice)}</span>
        </div>
      `;
    }

    // Get option display name from steps
    const getOptionDisplayName = (sectionKey, value) => {
      const option = this.findOptionInSteps(sectionKey, value);
      return option?.displayName || formatOptionValue(value);
    };

    // Add price details for selected options that have prices
    // Include all configurable options for Portland model (including panels)
    const detailKeys = [
      { key: 'kitchen', alt: 'kitchenConfig', label: 'Kitchen' },
      { key: 'orientation', label: 'Orientation' },
      { key: 'exterior', alt: 'windowConfig', label: 'Exterior' },
      { key: 'roof', alt: 'roofType', label: 'Roof' },
      { key: 'roofAddon', label: 'Roof Addon' },
      { key: 'frontPanel', label: 'Front Panel' },
      { key: 'backPanel', label: 'Back Panel' },
      { key: 'leftPanel', label: 'Left Panel' },
      { key: 'rightPanel', label: 'Right Panel' },
      { key: 'siding', alt: 'sidingType', label: 'Siding' },
      { key: 'exteriorAddon', label: 'Exterior Addon' },
      { key: 'mainFloorFinish', nested: ['flooring', 'main'], label: 'Main Floor' },
      { key: 'bathFloorFinish', nested: ['flooring', 'bath'], label: 'Bath Floor' },
      { key: 'fixtureFinish', nested: ['finishes', 'fixture'], label: 'Fixtures' },
      { key: 'counterFinish', nested: ['finishes', 'countertop'], label: 'Counter' },
      { key: 'cabinetFinish', nested: ['finishes', 'cabinets'], label: 'Cabinets' },
      { key: 'sidingColor', label: 'Siding Color' },
      { key: 'doorColor', label: 'Door' },
      { key: 'fasciaColor', label: 'Fascia' },
      { key: 'soffitColor', label: 'Soffit' },
      { key: 'eavesColor', label: 'Eaves' }
    ];

    detailKeys.forEach(({ key, alt, nested, label }) => {
      const value = this.currentConfig[key] || (alt && this.currentConfig[alt]) || (nested && this.currentConfig[nested[0]]?.[nested[1]]);
      if (!value) return;
      
      const option = this.findOptionInSteps(key, value) || (alt && this.findOptionInSteps(alt, value)) || (nested && this.findNestedOptionInSteps(nested[0], nested[1], value));
      const displayName = option?.displayName || formatOptionValue(value);
      const price = option?.price || 0;
      
      html += `
        <div class="price-detail-item">
          <span class="price-detail-label">${label}: ${displayName}</span>
          <span class="price-detail-price">${formatPrice(price)}</span>
        </div>
      `;
    });

    return html;
  }

  buildCTASectionHTML() {
    // Get CTA config from modelConfig or use Portland-specific defaults
    const productName = this.modelsData?.name || this.selectedVariant?.title || 'Portland';
    
    // Portland-specific default configuration
    const defaultCta = {
      calculator: {
        icon: `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="40" height="40" rx="8" fill="#FDB913" fill-opacity="0.15"/>
          <path d="M13 14h14v12H13V14z" stroke="#FDB913" stroke-width="1.5" fill="none"/>
          <path d="M16 18h8M16 21h8M16 24h4" stroke="#FDB913" stroke-width="1.5" stroke-linecap="round"/>
        </svg>`,
        title: 'Need A Quick Estimate?',
        link: '/pages/project-cost-calculator',
        linkText: 'TRY PROJECT COST CALCULATOR'
      },
      consultation: {
        title: 'Schedule a FREE Online Consultation',
        description: 'Select the available time that works for you to explore your project goals, budget, build site, and initial design.',
        link: '/pages/consultation',
        buttonText: 'BOOK A FREE CONSULTATION'
      },
      financing: {
        logos: [
          `<img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/acorn-finance-logo.png?v=1735000000" alt="Acorn Finance" style="height: 24px; width: auto;">`,
          `<img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/renofi-logo.png?v=1735000000" alt="RenoFi" style="height: 24px; width: auto;">`
        ],
        title: 'Monthly Financing Available!',
        subtitle: 'Plan smarter with the right financing',
        link: '/pages/financing',
        linkText: 'EXPLORE FINANCING OPTIONS'
      }
      
    };
    
    // PDF icon SVG for downloads
    const pdfIcon = `<svg viewBox="0 0 24 24" fill="none" style="width: 24px; height: 24px;">
      <g clip-path="url(#clip0_1618_10352)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.67107 22.95C-1.00893 20.91 0.77757 18.48 2.60607 17.445C3.62157 16.8735 4.88607 16.476 6.25107 16.101C5.84487 16.8749 5.3967 17.6261 4.90857 18.351C4.50698 18.5122 4.11573 18.6981 3.73707 18.9075C2.77407 19.4505 1.48707 20.4 2.27757 21.5925C3.04707 22.7535 4.28757 21.477 4.87257 20.8035C5.61957 20.0415 6.46257 18.8385 7.21257 17.5185C8.00607 16.1235 8.72757 14.5485 9.41757 12.8835C10.0401 11.3835 10.5936 9.83855 11.0076 8.39855C11.4276 6.95855 11.7111 5.62505 11.8161 4.50005C11.9016 3.56555 11.8611 2.19005 10.6311 2.37005C9.40107 2.55005 9.41607 3.99005 9.70857 5.11505C9.79457 5.45005 9.90257 5.79755 10.0326 6.15755C9.94057 6.58855 9.82907 7.04355 9.69807 7.52255C9.58807 7.91855 9.46807 8.32405 9.33807 8.73905C8.80876 7.706 8.38995 6.61999 8.08857 5.49905C7.56357 3.47405 7.54107 0.654046 10.2636 0.0690461C12.9861 -0.515954 13.6686 2.57405 13.4886 4.55405C13.3386 6.15905 12.8931 8.12405 12.2181 10.149C12.6501 10.8645 13.1211 11.5815 13.6206 12.279C14.1456 13.0155 14.8356 13.7685 15.4056 14.424C17.4156 14.274 19.3956 14.373 20.8806 14.6535C22.9206 15.039 24.7656 16.3185 23.6856 18.7635C22.6041 21.2085 19.9356 20.8485 18.1656 19.71C16.6893 18.7558 15.3942 17.5471 14.3406 16.14H14.3361C13.2173 14.4622 12.1907 12.7246 11.2611 10.935C10.8171 12.2745 10.1661 14.07 9.61107 15.27C10.3206 15.105 11.0661 14.97 11.8011 14.8575C12.1026 15.3645 12.3906 15.8595 12.6861 16.3395C12.4511 16.3745 12.2061 16.4135 11.9511 16.4565C11.0281 16.6135 10.1231 16.8015 9.23607 17.0205C9.01207 17.4505 8.79507 17.8745 8.58507 18.2925C7.83657 19.7745 7.14657 21.1425 6.29007 22.1175C4.92057 23.6775 2.36007 24.9525 0.68007 22.9125L0.67107 22.95ZM16.4211 15.93C17.3196 16.848 18.1611 17.55 18.9861 18.09C19.9881 18.735 21.1461 19.1115 21.6861 17.985C22.2366 16.8555 21.2196 16.29 20.1561 16.08C19.1076 15.8805 17.8611 15.8715 16.4061 15.924L16.4211 15.93Z" fill="black"/>
      </g>
      <defs>
        <clipPath id="clip0_1618_10352">
          <rect width="24" height="24" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;
    
    // Build downloads from variant metafield documents
    const variantDocs = (this.variantDocuments || []).map(doc => ({
      icon: pdfIcon,
      title: doc.alt || doc.filename || `${productName} Document`,
      url: doc.url
    }));
    
    const ctaConfig = this.modelConfig?.cta || defaultCta;
    const downloads = variantDocs.length ? variantDocs : (this.modelConfig?.downloads || []);

    let html = '';

    // CTA Cards section
    const calculator = ctaConfig.calculator || defaultCta.calculator;
    const financing = ctaConfig.financing || defaultCta.financing;
    
    html += `<div class="d-flex flex-column gap-2 pb-5" data-summary-cta-section>`;
     // Consultation section
     const consultation = ctaConfig.consultation || defaultCta.consultation;
     html += `
       <div class="py-4 border-top border-gray-200" data-summary-consultation>
         <h5 class="mb-2 d-none" data-consultation-title>${consultation.title}</h5>
         <p class="mb-lg-5 mb-3 pe-xl-6" style="letter-spacing: -0.02em;" data-consultation-description>${consultation.description}</p>
         <a href="${consultation.link}" class="btn btn-primary w-100" data-consultation-btn target="_blank">${consultation.buttonText}</a>
       </div>
     `;
    
    // Calculator card
    html += `
      <div class="calculator-card position-relative overflow-hidden d-flex flex-wrap align-items-center justify-content-between bg-primary-100 rounded gap-2" data-cta-card="calculator">
        <div class="d-flex align-items-center gap-lg-6 gap-3 me-3" data-cta-content>
          <div class="calculator-icon position-absolute">
            <img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/calculator.svg?v=1767594243" alt="Calculator" title="Calculator" class="w-100 h-100"/>
          </div>
          <span class="fw-medium" data-cta-text>${calculator.title}</span>
        </div>
        <a href="${calculator.link}" class="d-flex align-items-center gap-2 text-decoration-none fw-medium" data-cta-link target="_blank">
          ${calculator.linkText}
          <svg viewBox="0 0 11 11" fill="none" style="width: 11px; height: 11px;">
            <path d="M5.5 0.5L10.5 5.5M10.5 5.5L5.5 10.5M10.5 5.5L0.5 5.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>
    `;

    

    // Financing card (hidden by default like Aspect)
    html += `
      <div class="financing-card d-flex flex-wrap align-items-center justify-content-between bg-secondary rounded gap-2 d-none" data-cta-card="financing">
        <div class="d-flex align-items-center gap-lg-6 gap-3 me-3" data-cta-content>
          <div class="d-flex flex-column gap-2" data-financing-logos>
            <img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/acorn.png?v=1767596474" class="acorn-logo" alt="Acorn Finance" title="Acorn Finance"/>
            <img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/renofi.png?v=1767596473" class="renofi-logo" alt="RenoFi" title="RenoFi"/>
          </div>
          <div class="d-flex flex-column" data-financing-text>
            <p class="fw-medium mb-1">${financing.title}</p>
            <span class="fs-sm" style="letter-spacing: -0.01em;">${financing.subtitle}</span>
          </div>
        </div>
        <a href="${financing.link}" class="d-flex align-items-center gap-2 text-decoration-none text-black fw-medium" data-cta-link target="_blank">
          ${financing.linkText}
          <svg viewBox="0 0 11 11" fill="none" style="width: 11px; height: 11px;">
            <path d="M5.5 0.5L10.5 5.5M10.5 5.5L5.5 10.5M10.5 5.5L0.5 5.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>
    `;

    html += `</div>`;

   

    // Downloads section
    if (downloads.length) {
      html += `
        <div class="py-4 border-top border-gray-200" data-summary-downloads>
          <h5 class="mb-3" data-downloads-title>Downloads</h5>
          ${downloads.map(dl => `
            <a href="${dl.url || '#'}" class="d-flex align-items-center gap-3 text-decoration-none text-black" data-download-link target="_blank">
              <span data-download-icon>${dl.icon}</span>
              <span class="text-decoration-underline">${dl.title || ''}</span>
            </a>
          `).join('')}
        </div>
      `;
    }

    return html;
  }

  buildDesignDetailsHTML(variantTitle) {
    const config = this.currentConfig || {};
    const variant = this.selectedVariant || {};
    const modelData = this.modelsData || {};

    // Helper to get option data (including hex, displayName) from steps
    const getOptionData = (sectionKey, value) => {
      if (!value) return null;
      for (const step of this.steps) {
        // Check direct options
        const option = step.options?.[sectionKey]?.options?.find(o => o.value === value);
        if (option) return option;
        // Check nested options (e.g., sidingColor.main)
        if (sectionKey.includes('.')) {
          const [parent, child] = sectionKey.split('.');
          const nested = step.options?.[parent]?.[child]?.options?.find(o => o.value === value);
          if (nested) return nested;
        }
        // Check panel-based sections
        if (step.sections) {
          const section = step.sections.find(s => s.id === sectionKey);
          if (section) {
            const option = section.options?.find(o => o.value === value);
            if (option) return option;
          }
        }
      }
      return null;
    };

    // Helper to get config value (handles nested like flooring.main)
    const getConfigValue = (key, nestedKey) => {
      if (nestedKey) {
        return config[key]?.[nestedKey] || config[`${key}${nestedKey.charAt(0).toUpperCase()}${nestedKey.slice(1)}`];
      }
      return config[key];
    };

    // Helper to render material item with texture image preview (for flooring and siding)
    const renderTextureItem = (label, value, sectionKey) => {
      if (!value) return '';
      const option = getOptionData(sectionKey, value);
      // Get texture image from option - check multiple possible properties
      const textureImage = option?.texture || option?.thumbnail || option?.image || option?.preview;
      const displayName = option?.displayName || formatOptionValue(value);
      
      if (textureImage) {
        // Render with texture image
        return `
          <div class="material-item d-flex align-items-center gap-2 mb-3">
            <div class="material-swatch material-swatch--texture overflow-hidden">
              <img src="${textureImage}" alt="${displayName}" loading="lazy" style="width: 100%; height: 100%; object-fit: cover;">
            </div>
            <div class="material-info">
              <span class="d-block mb-1 fs-sm" style="letter-spacing: -0.01em;">${label}</span>
              <span class="d-block">${displayName}</span>
            </div>
          </div>
        `;
      } else {
        // Fallback to hex color if no texture image available
        const hex = option?.hex || option?.color?.hex || '#888888';
        return `
          <div class="material-item d-flex align-items-center gap-2 mb-3">
            <div class="material-swatch overflow-hidden" style="background: ${hex};"></div>
            <div class="material-info">
              <span class="d-block mb-1 fs-sm" style="letter-spacing: -0.01em;">${label}</span>
              <span class="d-block">${displayName}</span>
            </div>
          </div>
        `;
      }
    };

    // Helper to render material item only if value exists (with solid color)
    const renderMaterialItem = (label, value, sectionKey) => {
      if (!value) return '';
      const option = getOptionData(sectionKey, value);
      const hex = option?.hex || option?.color?.hex || '#888888';
      return `
        <div class="material-item d-flex align-items-center gap-2 mb-3">
          <div class="material-swatch overflow-hidden" style="background: ${hex};"></div>
          <div class="material-info">
            <span class="d-block mb-1 fs-sm" style="letter-spacing: -0.01em;">${label}</span>
            <span class="d-block">${option?.displayName || formatOptionValue(value)}</span>
          </div>
        </div>
      `;
    };

    // Helper to render color item only if value exists (RGB color representation)
    const renderColorItem = (label, sectionKey, value) => {
      if (!value) return '';
      const option = getOptionData(sectionKey, value);
      const hex = option?.hex || option?.color?.hex || '#888888';
      return `
        <div class="color-item d-flex align-items-center gap-2">
          <div class="color-swatch overflow-hidden" style="background: ${hex};"></div>
          <div class="color-info">
            <span class="d-block mb-1 fs-sm">${label}</span>
            <span class="d-block">${option?.displayName || formatOptionValue(value)}</span>
          </div>
        </div>
      `;
    };

    // Helper to render detail row only if value exists
    const renderDetailRow = (label, value) => {
      if (!value) return '';
      return `
        <div class="design-detail-col">
          <h6 class="mb-3">${label}</h6>
          <p class="mb-0">${formatOptionValue(value)}</p>
        </div>
      `;
    };

    // Build model specs line
    const specParts = [];
    if (modelData.depth) specParts.push(`Depth: ${modelData.depth}`);
    if (modelData.length) specParts.push(`Length: ${modelData.length}`);
    if (modelData.squareFootage) specParts.push(`${modelData.squareFootage} sq. ft.`);
    const specsLine = specParts.length ? `<p>${specParts.join(' | ')}</p>` : '';

    // Build model features line
    const featureParts = [];
    if (config.windowOption || config.exterior) featureParts.push(formatOptionValue(config.windowOption || config.exterior));
    if (config.roofType || config.roof) featureParts.push(`${formatOptionValue(config.roofType || config.roof)} Roof`);
    const featuresLine = featureParts.length ? `<p class="mb-2">${featureParts.join(' | ')}</p>` : '';

    // Build interior line
    const interiorParts = [];
    if (config.layout) interiorParts.push(formatOptionValue(config.layout));
    if (config.kitchenConfig || config.kitchen) interiorParts.push(formatOptionValue(config.kitchenConfig || config.kitchen));
    const interiorLine = interiorParts.length ? `<p>${interiorParts.join(' | ')}</p>` : '';

    // Get material values
    const mainFloor = getConfigValue('mainFloorFinish') || getConfigValue('flooring', 'main');
    const bathFloor = getConfigValue('bathFloorFinish') || getConfigValue('flooring', 'bath');
    const fixture = getConfigValue('fixtureFinish') || getConfigValue('finishes', 'fixture');
    const counter = getConfigValue('counterFinish') || getConfigValue('finishes', 'countertop');
    const cabinet = getConfigValue('cabinetFinish') || getConfigValue('finishes', 'cabinets');
    const siding = getConfigValue('sidingType') || getConfigValue('siding');
    // Note: Portland does NOT have Trim option

    // Get color values
    const sidingColorValue = typeof config.sidingColor === 'string' ? config.sidingColor : config.sidingColor?.main;
    const accentColorValue = typeof config.sidingColor === 'object' ? config.sidingColor?.accent : config.accentColor;

    // Check if interior package is "Shell Only" - normalize the value for comparison
    const interiorPackageValue = config.interiorPackage || config.layout || '';
    const isShellOnly = interiorPackageValue && 
      String(interiorPackageValue).toLowerCase().replace(/[\s_-]/g, '').includes('shellonly');

    // Debug: Log all config values to help identify correct keys
    console.log('[Portland Summary] ========== CONFIG DEBUG ==========');
    console.log('[Portland Summary] All config keys:', Object.keys(config));
    console.log('[Portland Summary] Full config:', JSON.stringify(config, null, 2));
    console.log('[Portland Summary] isShellOnly:', isShellOnly);
    // Log any key containing specific terms
    Object.keys(config).forEach(key => {
      const keyLower = key.toLowerCase();
      if (keyLower.includes('wainscot') || keyLower.includes('roof') || keyLower.includes('floor') || keyLower.includes('addon')) {
        console.log(`[Portland Summary] Found relevant key: ${key} = ${config[key]}`);
      }
    });
    console.log('[Portland Summary] =================================');

    // Build flooring section - only if NOT Shell Only
    let flooringItems = '';
    if (!isShellOnly) {
      const flooringItemsArray = [
        mainFloor ? renderTextureItem('Main Flooring', mainFloor, 'mainFloorFinish') : '',
        bathFloor ? renderTextureItem('Bath Flooring', bathFloor, 'bathFloorFinish') : ''
      ].filter(Boolean);
      flooringItems = flooringItemsArray.join('');
    }

    // Build finishes section
    const finishItems = [
      fixture ? renderMaterialItem('Fixtures', fixture, 'fixtureFinish') : '',
      counter ? renderMaterialItem('Countertop', counter, 'counterFinish') : '',
      cabinet ? renderMaterialItem('Cabinets', cabinet, 'cabinetFinish') : ''
    ].filter(Boolean).join('');

    // Build siding item with texture preview
    // Try multiple section keys since Portland might use 'siding' or 'sidingType'
    let sidingItem = '';
    if (siding) {
      // Determine the correct section key for siding lookup
      const sidingSectionKey = config.sidingType ? 'sidingType' : 
                               config.siding ? 'siding' : 'sidingType';
      console.log('[Portland Summary] Siding value:', siding, 'using sectionKey:', sidingSectionKey);
      sidingItem = renderTextureItem('Siding', siding, sidingSectionKey);
    }

    // Find wainscot value - check exteriorAddon (wainscot is stored there) or keys containing "wainscot"
    let wainscotValue = null;
    
    // Check exteriorAddon first - wainscot is typically stored here with value like "metal-wainscot"
    if (config.exteriorAddon) {
      const addonValue = String(config.exteriorAddon).toLowerCase();
      if (addonValue.includes('wainscot')) {
        wainscotValue = config.exteriorAddon;
        console.log('[Portland Summary] Found wainscot in exteriorAddon:', wainscotValue);
      }
    }
    
    // Fallback: check for any key containing "wainscot"
    if (!wainscotValue) {
      for (const key of Object.keys(config)) {
        if (key.toLowerCase().includes('wainscot')) {
          wainscotValue = config[key];
          console.log('[Portland Summary] Found wainscot key:', key, 'value:', wainscotValue);
          break;
        }
      }
    }

    // Find roof value - check all possible config keys
    let roofValue = config.roof || config.roofStyle || config.roofType || config.roofOptions || config.roofOption;
    if (!roofValue) {
      for (const key of Object.keys(config)) {
        if (key.toLowerCase().includes('roof') && !key.toLowerCase().includes('addon')) {
          roofValue = config[key];
          console.log('[Portland Summary] Found roof key:', key, 'value:', roofValue);
          break;
        }
      }
    }

    // Build colors section (RGB color representation) - only for user-selectable colors in Portland
    // Note: Portland has Fascia and Soffit but NO Trim option
    const colorItemsArray = [];
    
    if (sidingColorValue) {
      colorItemsArray.push(renderColorItem('Siding Color', 'sidingColor', sidingColorValue));
    }
    if (config.doorColor) {
      colorItemsArray.push(renderColorItem('Door', 'doorColor', config.doorColor));
    }
    if (config.fasciaColor) {
      colorItemsArray.push(renderColorItem('Fascia', 'fasciaColor', config.fasciaColor));
    }
    if (config.soffitColor) {
      colorItemsArray.push(renderColorItem('Soffit', 'soffitColor', config.soffitColor));
    }
    if (accentColorValue) {
      colorItemsArray.push(renderColorItem('Accent', 'sidingColor.accent', accentColorValue));
    }
    if (config.eavesColor || config.eaves) {
      const eavesValue = config.eavesColor || config.eaves;
      const eavesSectionKey = config.eavesColor ? 'eavesColor' : 'eaves';
      colorItemsArray.push(renderColorItem('Eaves', eavesSectionKey, eavesValue));
    }
    // Window casement color is auto-derived from trim color in the 3D viewer
    // (not user-selectable), so it's not shown in the summary.
    
    const colorItems = colorItemsArray.join('');

    // Build detail options rows - include all configurable options
    const detailRows = [
      { label: 'INTERIOR LAYOUT', value: config.layout },
      { label: 'EXTERIOR WINDOW OPTION', value: config.windowOption || config.exterior },
      { label: 'KITCHEN LAYOUT', value: config.kitchenConfig || config.kitchen },
      { label: 'ROOF OPTION', value: roofValue },
      { label: 'ROOF ADDON', value: config.roofAddon },
      { label: 'ORIENTATION', value: config.orientation },
      { label: 'WAINSCOT', value: wainscotValue },
      { label: 'EXTERIOR ADDON', value: config.exteriorAddon && !String(config.exteriorAddon).toLowerCase().includes('wainscot') ? config.exteriorAddon : null }
    ].filter(r => r.value);

    // Get includes/excludes from modelConfig or use defaults
    const includes = this.modelConfig?.includes || [
      'Exterior Shell with Lap Siding',
      'Interior Framing',
      'Interior Insulation and flooring',
      'Electrical, lighting, appliance package',
      'Complete kitchen and/or bathroom package',
      'Door and window hardware'
    ];
    const excludes = this.modelConfig?.excludes || [
      'Foundation and site utilities',
      'Drywall (included with ProAssembly service)',
      'Permit fees',
      'Sales tax as applicable'
    ];

    let html = `<div class="summary-design-details"><h5 class="mb-md-5 mb-3">Design Details</h5>`;

    // MODEL & INTERIOR section
    html += `
      <div class="design-details-grid">
        <div class="design-detail-col">
          <h6 class="mb-md-3 mb-2">MODEL</h6>
          ${variantTitle ? `<p class="mb-2">${variantTitle}</p>` : ''}
          ${featuresLine}
          ${specsLine}
        </div>
        ${(interiorLine || variant.bedBath) ? `
        <div class="design-detail-col">
          <h6 class="mb-md-3 mb-2">INTERIOR</h6>
          ${interiorLine}
          ${variant.bedBath ? `<p class="mb-2">${variant.bedBath}</p>` : ''}
        </div>
        ` : ''}
      </div>
    `;

    // Detail options rows (in pairs)
    if (detailRows.length > 0) {
      for (let i = 0; i < detailRows.length; i += 2) {
        html += `<div class="design-details-grid design-details-options">`;
        html += renderDetailRow(detailRows[i].label, detailRows[i].value);
        if (detailRows[i + 1]) {
          html += renderDetailRow(detailRows[i + 1].label, detailRows[i + 1].value);
        }
        html += `</div>`;
      }
    }

    html += `</div>`;

    // Flooring & Finishes section
    if (flooringItems || finishItems) {
      html += `<div class="summary-materials-section"><div class="design-details-grid">`;
      if (flooringItems) {
        html += `<div class="design-detail-col"><h6 class="mb-3">FLOORING</h6>${flooringItems}</div>`;
      }
      if (finishItems) {
        html += `<div class="design-detail-col"><h6 class="mb-3">FINISHES</h6>${finishItems}</div>`;
      }
      html += `</div></div>`;
    }

    // Exterior/Siding section (with texture preview)
    // Note: Portland does NOT have Trim option
    if (sidingItem) {
      html += `<div class="summary-materials-section"><div class="design-details-grid">`;
      html += `<div class="design-detail-col"><h6 class="mb-3">SIDING</h6>${sidingItem}</div>`;
      html += `</div></div>`;
    }

    // Colors section
    if (colorItems) {
      html += `
        <div class="summary-colors-section">
          <h6 class="mb-3">COLORS</h6>
          <div class="colors-grid">${colorItems}</div>
        </div>
      `;
    }

    // Elevations section - show images based on selected panel options
    const elevationImages = this.getElevationImages();
    
    if (elevationImages.front || elevationImages.back || elevationImages.left || elevationImages.right) {
      html += `
        <div class="summary-elevations-section d-flex flex-column gap-4">
          ${elevationImages.front ? `
          <div class="elevation-view">
            <h6 class="mb-3">FRONT</h6>
            <div class="elevation-image"><img src="${elevationImages.front}" alt="Front elevation" loading="lazy"></div>
          </div>
          ` : ''}
          ${elevationImages.back ? `
          <div class="elevation-view">
            <h6 class="mb-3">BACK</h6>
            <div class="elevation-image"><img src="${elevationImages.back}" alt="Back elevation" loading="lazy"></div>
          </div>
          ` : ''}
          ${elevationImages.left ? `
          <div class="elevation-view">
            <h6 class="mb-3">LEFT</h6>
            <div class="elevation-image"><img src="${elevationImages.left}" alt="Left elevation" loading="lazy"></div>
          </div>
          ` : ''}
          ${elevationImages.right ? `
          <div class="elevation-view">
            <h6 class="mb-3">RIGHT</h6>
            <div class="elevation-image"><img src="${elevationImages.right}" alt="Right elevation" loading="lazy"></div>
          </div>
          ` : ''}
        </div>
      `;
    }

    // Additional options section (always show)
    html += `
      <div class="summary-additional-options">
        <h6 class="mb-4">ADDITIONAL OPTIONS</h6>
        <p class="mb-3">Exterior: ${config.exteriorOptions || 'None'}</p>
        <p class="mb-0">Interior: ${config.interiorOptions || 'None'}</p>
      </div>
    `;

    // Includes/Excludes section (always show)
    html += `
      <div class="summary-includes-excludes">
        <div class="includes-excludes-grid">
          <div class="includes-col mb-sm-0 mb-5">
            <h6 class="mb-3">INCLUDES</h6>
            <ul class="ps-xl-5 mb-0">${includes.map(item => `<li>${item}</li>`).join('')}</ul>
          </div>
          <div class="excludes-col">
            <h6 class="mb-3">EXCLUDES</h6>
            <ul class="ps-xl-5 mb-0">${excludes.map(item => `<li>${item}</li>`).join('')}</ul>
          </div>
        </div>
      </div>
    `;

    return html;
  }

  findOptionInSteps(sectionKey, value) {
    for (const step of this.steps) {
      // Check orientation-based format (options)
      const option = step.options?.[sectionKey]?.options?.find(opt => opt.value === value);
      if (option) return option;
      
      // Check panel-based format (sections)
      if (step.sections) {
        const section = step.sections.find(s => s.id === sectionKey);
        if (section) {
          const sectionOption = section.options?.find(opt => opt.value === value);
          if (sectionOption) return sectionOption;
        }
      }
    }
    return null;
  }

  findNestedOptionInSteps(parentKey, childKey, value) {
    for (const step of this.steps) {
      const option = step.options?.[parentKey]?.[childKey]?.options?.find(opt => opt.value === value);
      if (option) return option;
    }
    return null;
  }

  /**
   * Get elevation images from selected panel options
   * Uses the image/thumbnail/texture property from step 2 panel options
   * Falls back to checking modelConfig.images.elevations if options don't have images
   */
  getElevationImages() {
    const config = this.currentConfig || {};
    const images = {
      front: null,
      back: null,
      left: null,
      right: null
    };

    // Get image for each panel from the selected option
    const panelKeys = [
      { key: 'frontPanel', side: 'front' },
      { key: 'backPanel', side: 'back' },
      { key: 'leftPanel', side: 'left' },
      { key: 'rightPanel', side: 'right' }
    ];

    panelKeys.forEach(({ key, side }) => {
      const value = config[key];
      if (value) {
        const option = this.findOptionInSteps(key, value);
        // Check multiple possible image properties
        const optionImage = option?.image || option?.elevationImage || option?.panelImage || option?.thumbnail || option?.texture;
        if (optionImage) {
          images[side] = optionImage;
        }
      }
    });

    // Fallback: Check if modelConfig has elevation images (like Aspect)
    // This allows using dedicated elevation images if configured in JSON
    if (!images.front && !images.back && !images.left && !images.right) {
      const selectedKey = config.frontPanel || config.exterior || config.windowOption || config.glass || 'default';
      const configElevations = this.modelConfig?.images?.elevations?.[selectedKey] || 
                               this.modelConfig?.images?.elevations?.default ||
                               this.modelConfig?.elevationImages;
      if (configElevations) {
        images.front = configElevations.front || null;
        images.back = configElevations.back || null;
        images.left = configElevations.left || null;
        images.right = configElevations.right || null;
      }
    }

    return images;
  }

  setupThankYouEventListeners(panel) {
    const editLink = $('[data-edit-design]', panel);
    console.log('[Portland] setupThankYouEventListeners - editLink found:', !!editLink);
    if (editLink) {
      editLink.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('[Portland] Edit Design clicked');
        
        // Get the first step name from the steps array
        const firstStep = this.steps[0];
        const firstTabName = firstStep?.name?.toLowerCase() || firstStep?.id?.toLowerCase() || 'floorplan';
        console.log('[Portland] Switching to tab:', firstTabName, 'from steps:', this.steps.map(s => s.name || s.id));
        
        // Switch to the first step tab
        this.switchTab(firstTabName);
        
        // Show the footer navigation buttons
        const footer = q('[data-panel-footer]', '.panel-footer', this.step2Section);
        if (footer) footer.style.display = '';
        
        // Remove summary class from configurator main
        const configuratorMain = $('[data-configurator-main]', this.step2Section);
        if (configuratorMain) {
          configuratorMain.classList.remove('product-summary-tab');
        }
      });
    }

    const priceToggle = $('[data-toggle-price-details]', panel);
    const priceContent = $('[data-price-details-content]', panel);
    if (priceToggle && priceContent) {
      // Setup smooth dropdown animation styles (Portland-specific)
      priceContent.style.overflow = 'hidden';
      priceContent.style.transition = 'max-height 0.35s ease-in-out, opacity 0.3s ease-in-out';
      priceContent.style.maxHeight = '0';
      priceContent.style.opacity = '0';
      priceContent.style.display = 'block'; // Always block, control visibility via max-height
      
      priceToggle.addEventListener('click', () => {
        const isCollapsed = priceContent.style.maxHeight === '0px' || priceContent.style.maxHeight === '0';
        
        if (isCollapsed) {
          // Expand: measure content height and animate to it
          priceContent.style.maxHeight = priceContent.scrollHeight + 'px';
          priceContent.style.opacity = '1';
        } else {
          // Collapse: animate to 0
          priceContent.style.maxHeight = '0';
          priceContent.style.opacity = '0';
        }
        
        const icon = priceToggle.querySelector('[data-toggle-icon], .toggle-icon');
        if (icon) {
          // Handle SVG icon (Portland uses SVG with use element)
          const svg = icon.querySelector('svg');
          if (svg) {
            icon.innerHTML = isCollapsed ? `<svg aria-hidden="true" focusable="false" style="width:15px; height:15px;">
                  <use href="#icon-modal-minus"></use>
                </svg>` : `<svg aria-hidden="true" focusable="false" style="width:15px; height:15px;">
                  <use href="#icon-modal-plus"></use>
                </svg>`;
          } else {
            icon.textContent = isCollapsed ? '−' : '+';
          }
        }
      });
    }

    const changePermit = $('[data-change-permit-plans]', panel);
    if (changePermit) changePermit.addEventListener('click', (e) => { e.preventDefault(); this.openPermitPlansDrawer(); });

    const changeFoundation = $('[data-change-foundation-type]', panel);
    if (changeFoundation) changeFoundation.addEventListener('click', (e) => { e.preventDefault(); this.openFoundationTypeDrawer(); });
  }

  // ============================================================================
  // FAQ SECTION
  // ============================================================================

  appendFullWidthFAQ() {
    const existingFAQ = q('[data-faq-container]', '.full-width-faq-container');
    if (existingFAQ) existingFAQ.remove();

    let faqConfig = null;
    try {
      const faqConfigEl = $('[data-faq-config]', this.step2Section);
      if (faqConfigEl) faqConfig = JSON.parse(faqConfigEl.textContent);
    } catch (e) { /* use defaults */ }

    const faqTitle = faqConfig?.title || this.modelConfig?.faq?.title || 'Frequently Asked Questions';
    const faqItems = faqConfig?.items || this.modelConfig?.faq?.items || [];
    
    // Don't show FAQ section if no items configured
    if (!faqItems.length) {
      log('No FAQ items configured');
      return;
    }

    const faqContainer = document.createElement('div');
    faqContainer.className = 'full-width-faq-container';
    faqContainer.setAttribute('data-faq-container', '');
    faqContainer.innerHTML = `
      <div class="summary-section-divider summary-section-divider--full" data-section-divider></div>
      <div class="row" data-faq-section>
        <div class="col-lg-5 col-12">
          <h3 class="mb-0" data-faq-title>${faqTitle}</h3>
        </div>
        <div class="col-lg-7 col-12">
          ${faqItems.map(item => `
            <div class="faq-item" data-faq-item>
              <button class="faq-question" data-faq-toggle>
                <span data-faq-question-text>${item.question}</span>
                <!-- <span data-faq-icon></span> -->
                <span>
                  <svg aria-hidden="true" focusable="false" style="width:15px; height:15px;">
                    <use href="#icon-modal-plus"></use>
                  </svg>
                </span>
              </button>
              <div class="faq-answer" data-faq-answer style="display: none;">${item.answer}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `;

    const configuratorLayout = q('[data-configurator-layout]', '.configurator-layout', this.step2Section);
    if (configuratorLayout) {
      configuratorLayout.parentNode.insertBefore(faqContainer, configuratorLayout.nextSibling);
    } else {
      this.step2Section.appendChild(faqContainer);
    }

    // Setup FAQ toggles
    faqContainer.querySelectorAll('[data-faq-toggle]').forEach(toggle => {
      toggle.addEventListener('click', () => {
        const content = toggle.nextElementSibling;
        const icon = toggle.querySelector('[data-faq-icon], .faq-icon');
        const isOpen = content.style.display !== 'none';
        content.style.display = isOpen ? 'none' : 'block';
        if (icon) icon.textContent = isOpen ? '+' : '−';
        toggle.classList.toggle('open', !isOpen);
      });
    });
  }

  // ============================================================================
  // PERMIT PLANS DRAWER - Using shared PermitManager
  // ============================================================================

  setupPermitPlansDrawer() {
    const rawSqFt = this.modelsData?.squareFootage;
    const parsedSqFt = typeof rawSqFt === 'string' ? parseInt(rawSqFt.replace(/[^0-9]/g, ''), 10) : parseInt(rawSqFt, 10);
    const sqFt = parsedSqFt || (parseInt(this.modelsData?.depth, 10) || 0) * (parseInt(this.modelsData?.length, 10) || 0);
    
    const defaultPermit = sqFt > 200 ? 'include' : 'exclude';

    if (this.permitManager) {
      this.permitManager.defaultSelected = defaultPermit;
      this.permitManager.basePrice = this.modelConfig?.permitPlanPrice || 6995;
      this.permitManager.reset();
      return;
    }

    this.permitManager = new window.ConfiguratorShared.PermitManager({
      configurator: this,
      drawerId: 'permit-plans-drawer',
      basePrice: this.modelConfig?.permitPlanPrice || 6995,
      defaultSelected: defaultPermit,
      formatPrice: formatPrice,
      onSelect: () => this.recalculateSummaryTotal(),
      step2Section: this.step2Section
    });
    this.permitManager.setup();
  }

  openPermitPlansDrawer() {
    this.permitManager?.open();
  }

  updateSummaryPermitDisplay() {
    this.permitManager?.updateSummaryDisplay();
  }

  // ============================================================================
  // FOUNDATION TYPE DRAWER
  // ============================================================================

  setupFoundationTypeDrawer() {
    this.foundationManager = new window.ConfiguratorShared.FoundationManager({
      configurator: this,
      drawerId: 'foundation-type-drawer',
      step2Section: this.step2Section
    });
    this.foundationManager.setup();
  }

  openFoundationTypeDrawer() {
    this.foundationManager?.open();
  }

  updateFoundationDrawerSelection() {
    this.foundationManager?.updateSelection();
  }

  handleFoundationOptionSelect(card) {
    this.foundationManager?.handleSelect(card);
  }

  updateSummaryFoundationDisplay() {
    this.foundationManager?.updateSummaryDisplay();
  }

  recalculateSummaryTotal() {
    const productPrice = this.basePrice + this.configPrice;
    const shippingPrice = normalizeNumber(this.finalPriceData?.shipping_cost, this.modelConfig?.shippingPrice || 0);
    const installationOptions = this.modelConfig?.services?.installation?.options || [];
    const selectedInstallation = installationOptions.find(opt => opt.value === this.currentConfig.installation);
    
    // Get installation price from API breakdown
    // For "shell-lifestyle" option, use lifestyle price from API breakdown
    let installationPrice = 0;
    const installationValue = this.currentConfig.installation || '';
    
    if (this.finalPriceData?.breakdown?.installation) {
      const breakdown = this.finalPriceData.breakdown.installation;
      
      // If user selected "shell-lifestyle", use lifestyle price from API breakdown
      if (installationValue.toLowerCase().includes('lifestyle')) {
        installationPrice = normalizeNumber(breakdown.lifestyle, 0);
      } else {
        // For other options, use the computed installation cost
        const computedCents = window.ConfiguratorShared.computeInstallationCost(
          breakdown,
          installationValue
        );
        installationPrice = computedCents > 0 ? computedCents : normalizeNumber(selectedInstallation?.price, 0);
      }
    } else {
      // Fallback if no API data available
      installationPrice = normalizeNumber(selectedInstallation?.price, 0);
    }
    const estimatedTotal = productPrice + shippingPrice + this.permitPlanPrice + installationPrice;

    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    const totalDisplay = $('[data-summary-total-price]', contentWrapper);
    if (totalDisplay) totalDisplay.textContent = formatPrice(estimatedTotal);

    const totalNote = q('[data-total-note]', '.total-note', contentWrapper);
    if (totalNote) {
      totalNote.innerHTML = this.getTotalNoteText();
    }

    // Update ALL price elements (header and mobile footer)
    const headerPrices = $$('[data-configurator-price]', this.step2Section);
    headerPrices.forEach(el => el.textContent = formatPrice(estimatedTotal));
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Generate the total note text based on installation selection
   * Installation options:
   * - "shell" or "product-only" = Product Only (no assembly)
   * - "lifestyle" = ProAssembly: Shell Only (has assembly)
   * - "kitchenbath" = ProAssembly: Shell + Lifestyle Interior (has assembly)
   * @returns {string} HTML string for the total note
   */
  getTotalNoteText() {
    const installation = (this.currentConfig?.installation || '').toLowerCase();
    // "shell" and "product-only" mean no assembly, anything else means assembly is included
    const noAssemblyValues = ['shell', 'product-only', 'product_only', 'productonly', ''];
    const hasAssembly = !noAssemblyValues.includes(installation);
    const hasPermitPlans = this.permitPlanSelected === 'include';
    
    let includes = ['product', 'delivery'];
    if (hasAssembly) includes.push('assembly');
    if (hasPermitPlans) includes.push('permit plans');
    
    // Format the includes list with proper grammar
    let includesText;
    if (includes.length === 2) {
      includesText = includes.join(' and ');
    } else {
      const last = includes.pop();
      includesText = includes.join(', ') + ', and ' + last;
    }
    
    return `Includes ${includesText}.<br>Excludes foundation, site utilities, HVAC, decks, upgrades, permitting, local taxes.`;
  }

  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  dispatchVariantChange(variantId, price) {
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variantId, price } }));
  }

  getSelectedVariantId() {
    const selectedCard = this.step1Section.querySelector('[data-variant-card].selected, .portland-variant-card.selected');
    return selectedCard?.dataset.variantId || null;
  }

  getConfiguration() {
    return {
      ...this.currentConfig,
      basePrice: this.basePrice,
      configPrice: this.configPrice,
      totalPrice: this.basePrice + this.configPrice,
      permitPlan: this.permitPlanSelected,
      permitPlanPrice: this.permitPlanPrice
    };
  }

  dispose() {
    if (this.step2ClickHandler && this.step2Section) {
      this.step2Section.removeEventListener('click', this.step2ClickHandler);
    }

    if (this.viewer3D) {
      this.viewer3D.dispose();
      this.viewer3D = null;
    }

    this.variantCards = [];
    this.thumbnails = [];
    this.steps = [];
    this.currentConfig = {};
    this.modelConfig = null;
    this.modelsData = null;
    this.userFormData = null;

    log('PortlandConfigurator disposed');
  }

  reset() {
    this.currentConfig = { ...this.defaultConfig };
    this.configPrice = 0;
    this.currentStep = 1;
    this.permitManager?.reset();
    this.foundationManager?.reset();
    this.userFormData = null;

    if (this.steps.length > 0) {
      this.switchTab(this.steps[0].name);
    }
  }

  handleStartOver() {
    this.reset();

    // Disable Summary tab immediately since form data is cleared
    this.updateSummaryTabState();

    const saveDesignBtn = $('[data-save-design]', this.step2Section);
    if (saveDesignBtn) {
      saveDesignBtn.textContent = 'SAVE DESIGN';
      delete saveDesignBtn.dataset.startOver;
    }

    const panelFooter = q('[data-panel-footer]', '.panel-footer', this.step2Section);
    if (panelFooter) panelFooter.style.display = '';

    const faqContainer = q('[data-faq-container]', '.full-width-faq-container');
    if (faqContainer) faqContainer.remove();

    // Restore layout to 8-4 columns
    this.restoreDefaultLayout();

    if (this.viewer3D) {
      this.applyConfigTo3D(this.currentConfig);
    }
  }

  /**
   * Restore default 8-4 column layout (from 6-6 thank you page layout)
   */
  restoreDefaultLayout() {
    const viewer = $('.configurator-viewer', this.step2Section);
    const panel = $('.configurator-panel', this.step2Section);
    if (viewer) {
      viewer.classList.remove('col-lg-6');
      viewer.classList.add('col-lg-8');
      const modelContainer = $('[data-model-viewer], .model-viewer-container', viewer);
      if (modelContainer) {
        modelContainer.style.removeProperty('max-width');
        modelContainer.style.removeProperty('margin');
      }
    }
    if (panel) {
      panel.classList.remove('col-lg-6');
      panel.classList.add('col-lg-4');
    }

    // Trigger 3D viewer resize after layout change
    setTimeout(() => {
      if (this.viewer3D?.sceneManager?.onResize) {
        this.viewer3D.sceneManager.onResize();
      }
    }, 50);
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function initPortlandConfigurator() {
  log('Initializing Portland Configurator...');
  
  if (!window.portlandConfigurator) {
    try {
      window.portlandConfigurator = new PortlandConfigurator();
      log('Portland Configurator created successfully');
    } catch (error) {
      console.error('Failed to initialize Portland Configurator:', error);
    }
  } else {
    log('Portland Configurator already exists');
  }
}

if (document.readyState === 'loading') {
  log('Document loading, waiting for DOMContentLoaded...');
  document.addEventListener('DOMContentLoaded', initPortlandConfigurator);
} else {
  log('Document already loaded, initializing immediately');
  initPortlandConfigurator();
}

// Also expose for manual re-initialization if needed
window.reinitPortlandConfigurator = () => {
  log('Manual re-initialization requested');
  window.portlandConfigurator = null;
  initPortlandConfigurator();
};
